import React, { useEffect, useMemo, useCallback, useState, useRef, useLayoutEffect } from 'react';
import { View, StyleSheet, ScrollView, Pressable, Platform, Animated, TextInput, useWindowDimensions, PanResponder, Easing, NativeModules, NativeEventEmitter } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';

import { useVideoPlayer, VideoView } from 'expo-video';
import { useAudioPlayer, useAudioPlayerStatus, setAudioModeAsync } from 'expo-audio';
import { useEvent } from 'expo';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useExploreStore, useExploreLightboxStore, useModelStore, useChatInputStore, useToastStore, useModalStore, useAuthStore } from '@/store';
import { trackEvent } from '@/constants/utils/analytics';
import useTranslation from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuthHook from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import Switch from '@/components/Inputs/Switch';
import ChatInputSendButton from '@/components/ChatInput/ChatInputSendButton';
import ExploreDetailParameters from '@/components/Explore/ExploreDetailParameters';
import Model3DViewer from '@/components/Model3D/Model3DViewer';
import { mintExploreImageForVideo, I2V_SLUG_PREFERENCES, resolveImageToVideoModelId } from '@/components/Explore/imageToVideoHelpers';
import { getExploreVideoConfirmCopy } from '@/components/Explore/exploreVideoConfirm';
import { buildExploreSelectedParameters } from '@/components/Explore/exploreVideoParams';
import copyText from '@/constants/utils/copy_text';
import { downloadMedia } from '@/constants/utils/mediaDownload';
import { mediumTap, notifySuccess } from '@/constants/utils/haptics';
import { getShadowThemeStyles, isDarkTheme, pickMusicGradient, pickMusicGradientMeta } from '@/constants/Colors';
import { hasPaidCredits, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';
import { classifyResponseError } from '@/constants/utils/response_errors';

const isWeb = Platform.OS === 'web';

function hashFloat(id, index, seed = 0) {
  let h = seed;
  const s = `${id}-${index}`;
  for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
  return (Math.abs(h) % 1000) / 1000;
}

const NUM_BARS = 24;

const LightboxWaveBar = React.memo(({ index, itemId, playing }) => {
  const baseHeight = 8 + hashFloat(itemId, index, 1) * 16;
  const anim = useRef(new Animated.Value(baseHeight)).current;

  useEffect(() => {
    if (playing) {
      const minH = 6;
      const maxH = 60;
      const duration = 300 + hashFloat(itemId, index, 2) * 500;
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(anim, { toValue: minH + hashFloat(itemId, index, 3) * (maxH - minH), duration, useNativeDriver: false }),
          Animated.timing(anim, { toValue: minH + hashFloat(itemId, index, 4) * (maxH - minH) * 0.3, duration: duration * 0.7, useNativeDriver: false }),
        ]),
      );
      loop.start();
      return () => loop.stop();
    }
    Animated.timing(anim, { toValue: baseHeight, duration: 300, useNativeDriver: false }).start();
  }, [playing]);

  return (
    <Animated.View style={{ height: anim, flex: 1, backgroundColor: 'rgba(255,255,255,0.5)', borderRadius: 2, minHeight: 4, opacity: playing ? 0.85 : 0.35 + hashFloat(itemId, index, 5) * 0.3 }} />
  );
});

// ── Animated Blob Background for Music ──────────────────────────────────────
// Each blob orbits around its anchor point (pctX/pctY, in % of container)
// using a parametric circular drift driven by a single phase value. This is
// dramatically smoother than chaining waypoints with Animated.timing because:
//   1. There are no discontinuities — the path is C¹-continuous (a sine/cosine
//      ellipse) so velocity never jumps at "waypoint" boundaries.
//   2. A single Animated.Value per blob (`phase`) means x, y and scale stay
//      perfectly in phase with each other — no out-of-sync resets across
//      multiple loops.
//   3. The phase loops 0 → 1 with linear easing, but the sin/cos interpolation
//      hides that under a smooth curve, so the loop boundary is invisible.
// Result: orbs that flow like lava lamp blobs instead of stuttering between
// hand-picked checkpoints.

const BLOB_CONFIGS = [
  // size, anchor offset (%), orbit radii (px), scale amplitude, period (ms),
  // initial phase offset (0–1) — phase offsets desync the blobs visually.
  { size: 460, pctX: -10, pctY: -14, rx: 140, ry: 110, ds: 0.18, dur: 14000, phase: 0.00 },
  { size: 380, pctX:  18, pctY: -20, rx: 120, ry: 150, ds: 0.22, dur: 17000, phase: 0.18 },
  { size: 540, pctX:  -6, pctY:  12, rx: 100, ry:  90, ds: 0.14, dur: 22000, phase: 0.37 },
  { size: 320, pctX:  22, pctY:  18, rx: 160, ry: 130, ds: 0.24, dur: 13000, phase: 0.55 },
  { size: 420, pctX: -22, pctY:   8, rx: 130, ry: 120, ds: 0.18, dur: 19000, phase: 0.71 },
  { size: 300, pctX:  10, pctY:  -6, rx: 150, ry: 110, ds: 0.22, dur: 15500, phase: 0.84 },
  { size: 400, pctX: -14, pctY:  -8, rx: 110, ry: 140, ds: 0.16, dur: 18500, phase: 0.92 },
];

const TWO_PI = Math.PI * 2;

const AnimatedBlob = React.memo(({ config, color, index, playing }) => {
  // A single 0→1 phase drives all transforms — keeps everything in sync and
  // produces a pure sine/cosine motion that has no abrupt transitions.
  // Because the interpolation outputs at phase=0 and phase=1 are identical
  // (full sine/cosine cycle), Animated.loop's reset is visually invisible.
  const phase = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const m = playing ? 0.55 : 1; // play state speeds up the drift slightly
    const dur = config.dur * m;
    phase.setValue(0);
    const loop = Animated.loop(
      Animated.timing(phase, {
        toValue: 1,
        duration: dur,
        easing: Easing.linear, // linear — the sin/cos warp below creates the curve
        useNativeDriver: true,
      }),
    );
    // Delay each blob's start so they desync visually instead of all peaking
    // at the same moment. The initial offset comes from `config.phase` which
    // stays consistent across mounts.
    const delay = ((config.phase || 0) * dur) % dur;
    const timer = setTimeout(() => loop.start(), delay);
    return () => { clearTimeout(timer); loop.stop(); };
  }, [playing]);

  // Sample the unit circle at 8 equally-spaced angles. More sample points =
  // smoother elliptical orbit (Animated.interpolate linearly tweens between
  // them). Using sin & cos guarantees output(0) == output(1) so the loop
  // boundary is seamless.
  const xSamples = useMemo(() => {
    const N = 8;
    const out = [];
    for (let i = 0; i <= N; i++) out.push(Math.cos((i / N) * TWO_PI) * config.rx);
    return out;
  }, [config.rx]);
  const ySamples = useMemo(() => {
    const N = 8;
    const out = [];
    // 90° phase shift + slight harmonic distortion so the path is a soft
    // figure-8 rather than a clean circle — feels more "lava lamp".
    for (let i = 0; i <= N; i++) {
      const t = (i / N) * TWO_PI;
      out.push(Math.sin(t) * config.ry + Math.sin(t * 2) * config.ry * 0.12);
    }
    return out;
  }, [config.ry]);
  const inputRange = useMemo(() => {
    const N = 8;
    return Array.from({ length: N + 1 }, (_, i) => i / N);
  }, []);

  const translateX = phase.interpolate({ inputRange, outputRange: xSamples });
  const translateY = phase.interpolate({ inputRange, outputRange: ySamples });
  // Scale breathes on a slower wave (cosine) so it doesn't beat with the orbit.
  const scale = phase.interpolate({
    inputRange: [0, 0.25, 0.5, 0.75, 1],
    outputRange: [
      1 + config.ds * Math.cos(0),
      1 + config.ds * Math.cos(TWO_PI * 0.25),
      1 + config.ds * Math.cos(TWO_PI * 0.5),
      1 + config.ds * Math.cos(TWO_PI * 0.75),
      1 + config.ds * Math.cos(TWO_PI),
    ],
  });

  const opacity = 0.42 + (index % 4) * 0.07;
  const halfSize = config.size / 2;

  return (
    <Animated.View
      style={{
        position: 'absolute',
        left: `${50 + config.pctX}%`,
        top: `${50 + config.pctY}%`,
        marginLeft: -halfSize,
        marginTop: -halfSize,
        width: config.size,
        height: config.size,
        borderRadius: halfSize,
        backgroundColor: color,
        opacity,
        transform: [{ translateX }, { translateY }, { scale }],
      }}
    />
  );
});

function MusicBlobBackground({ gradient, playing }) {
  const blobColors = useMemo(() => {
    const [c1, c2] = gradient;
    return [c1, c2, c1, c2, `${c1}bb`, `${c2}bb`, c1];
  }, [gradient]);

  return (
    <View style={blobStyles.container} pointerEvents="none">
      <View style={blobStyles.blurWrap}>
        {BLOB_CONFIGS.map((cfg, i) => (
          <AnimatedBlob key={i} config={cfg} color={blobColors[i]} index={i} playing={playing} />
        ))}
      </View>
      <View style={blobStyles.overlay} />
    </View>
  );
}

const blobStyles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  blurWrap: {
    ...StyleSheet.absoluteFillObject,
    // Heavier blur smooths over the orbs' edges so they read as flowing
    // light rather than discrete shapes. Web only — native uses blur via
    // the orb's own large size + opacity blending, not a CSS filter.
    ...(isWeb && { filter: 'blur(90px)' }),
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
});

// ── Media Previews ──────────────────────────────────────────────────────────

function LightboxImage({ item }) {
  const ratio = item.metadata?.aspectRatio || (
    item.metadata?.width && item.metadata?.height
      ? item.metadata.width / item.metadata.height
      : 0.75
  );
  return (
    <View style={mediaStyles.imageWrap}>
      <Image
        source={{ uri: item.cdnPath }}
        style={[mediaStyles.image, { aspectRatio: ratio }]}
        contentFit="contain"
        placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
        transition={300}
      />
    </View>
  );
}

function MacOSLightboxVideo({ item }) {
  const posterUri = item.thumbnailPath || '';
  const player = useVideoPlayer(item.cdnPath, (p) => {
    p.loop = true;
    p.muted = false;
    p.play();
  });

  if (!item.cdnPath) {
    return (
      <View style={mediaStyles.videoWrap}>
        {posterUri ? (
          <Image
            source={{ uri: posterUri }}
            style={mediaStyles.videoThumbFill}
            contentFit="contain"
            placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
          />
        ) : null}
        <View style={mediaStyles.playOverlay} pointerEvents="none">
          <View style={mediaStyles.playCircle}>
            <Icon name="play" size={28} color="#fff" solid />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={mediaStyles.videoWrap}>
      <VideoView
        player={player}
        nativeControls
        contentFit="contain"
        style={mediaStyles.videoNativeView}
      />
    </View>
  );
}

function NativeLightboxVideo({ item }) {
  const [playing, setPlaying] = useState(true);
  const ratio = item.metadata?.aspectRatio || 16 / 9;
  const player = useVideoPlayer(item.cdnPath, (p) => {
    p.loop = true;
    p.muted = false;
    p.play();
  });
  const { status } = useEvent(player, 'statusChange', { status: player.status });

  useEffect(() => {
    if (!player) return;
    if (playing) player.play();
    else player.pause();
  }, [player, playing]);

  useEffect(() => {
    if (!player || !playing || status !== 'readyToPlay') return;
    try { player.play(); } catch (_) {}
  }, [player, playing, status]);

  const togglePlayback = useCallback(() => {
      if (!playing) trackEvent('explore', 'explore_video_play', item?.id);
      setPlaying(p => !p);
  }, [item?.id, playing]);

  return (
    <Pressable onPress={togglePlayback} style={mediaStyles.videoWrap}>
      {!playing && item.thumbnailPath && (
        <Image
          source={{ uri: item.thumbnailPath }}
          style={[mediaStyles.videoThumb, { aspectRatio: ratio }]}
          contentFit="cover"
          placeholder={item.blurhash ? { blurhash: item.blurhash } : undefined}
        />
      )}
      <VideoView
        player={player}
        style={[mediaStyles.videoView, !playing && !item.thumbnailPath && { opacity: 1 }, !playing && item.thumbnailPath && { position: 'absolute', opacity: 0 }]}
        contentFit="contain"
        nativeControls
        allowsFullscreen
        allowsPictureInPicture
      />
      {!playing && (
        <View style={mediaStyles.playOverlay}>
          <Pressable onPress={togglePlayback} style={mediaStyles.playCircle}>
            <Icon name="play" size={28} color="#fff" solid />
          </Pressable>
        </View>
      )}
    </Pressable>
  );
}

function LightboxVideo({ item }) {
  if (Platform.OS === 'macos') return <MacOSLightboxVideo item={item} />;
  return <NativeLightboxVideo item={item} />;
}

function formatMillis(ms) {
  if (!ms || ms <= 0 || !isFinite(ms)) return '0:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s < 10 ? '0' + s : s}`;
}

function LightboxMusic({ item, colors }) {
  // Ported from the legacy `expo-av` Audio.Sound API to `expo-audio` hooks,
  // which matches the rest of the app (AudioWaveformPlayer, etc.). On iOS
  // the old API could hang the JS bridge when a previous audio session
  // (voice/dictate mode) hadn't been fully torn down, which presented as
  // the whole lightbox "locking up" when a music item was tapped. The
  // expo-audio hooks own the native session lifecycle correctly, so
  // switching audio items (or closing the lightbox) reliably releases it.
  const player = useAudioPlayer(item.cdnPath);
  const status = useAudioPlayerStatus(player);

  const playing = !!status?.playing;
  const positionMillis = Math.max(0, Math.round((status?.currentTime || 0) * 1000));
  // Prefer the live duration from the loaded player; fall back to the metadata
  // we stamped on the item so the track/time UI isn't blank pre-load.
  const liveDurationMillis = status?.duration ? Math.round(status.duration * 1000) : 0;
  const fallbackDurationMillis = item.metadata?.duration
    ? Math.round(item.metadata.duration * 1000)
    : 0;
  const durationMillis = liveDurationMillis || fallbackDurationMillis;

  const [trackWidth, setTrackWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragRatio, setDragRatio] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverRatio, setHoverRatio] = useState(null);
  const dragRatioRef = useRef(null);

  // `item.gradient` is stamped by useExploreStore on fetch — using it here
  // (instead of recomputing) guarantees the lightbox shows the same colors
  // as the card the user just clicked. Fallback for items that bypassed the
  // store (shouldn't normally happen).
  const gradient = item.gradient || pickMusicGradient(item.id);
  const useDarkFg = typeof item.gradientDark === 'boolean'
    ? item.gradientDark
    : !!pickMusicGradientMeta(item.id).dark;
  const fgPrimary = useDarkFg ? '#000' : '#fff';
  const fgSecondary = useDarkFg ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.7)';
  const fgIcon = useDarkFg ? 'rgba(0,0,0,0.85)' : 'rgba(255,255,255,0.85)';

  // Ensure audio plays even when the iOS silent switch is on. Safe to call
  // repeatedly across mounts — expo-audio dedupes internally.
  useEffect(() => {
    setAudioModeAsync({ playsInSilentMode: true }).catch(() => {});
  }, []);

  // Auto-play as soon as the player is ready. We intentionally don't depend
  // on `player` identity since `useAudioPlayer` returns a stable instance for
  // a given source.
  useEffect(() => {
    if (!player) return;
    trackEvent('explore', 'explore_music_play', item.id);
    try { player.play(); } catch (_) {}
    return () => {
      try { player.pause(); } catch (_) {}
    };
  }, [player, item.id]);

  // When a track finishes, rewind to 0 and stay paused so the play button
  // reappears (matches the prior expo-av behavior).
  useEffect(() => {
    if (!player) return;
    if (status?.didJustFinish) {
      try {
        player.pause();
        player.seekTo(0);
      } catch (_) {}
    }
  }, [player, status?.didJustFinish]);

  const toggle = useCallback(() => {
    if (!player) return;
    try {
      if (playing) player.pause();
      else player.play();
    } catch (_) {}
  }, [player, playing]);

  const seekToRatio = useCallback((ratio) => {
    if (!player || !durationMillis) return;
    const targetSec = Math.max(0, Math.min(durationMillis, Math.round(durationMillis * ratio))) / 1000;
    try { player.seekTo(targetSec); } catch (_) {}
  }, [player, durationMillis]);

  const updateDragFromX = useCallback((x) => {
    if (!trackWidth) return;
    const clampedX = Math.max(0, Math.min(trackWidth, x));
    const ratio = clampedX / trackWidth;
    dragRatioRef.current = ratio;
    setDragRatio(ratio);
  }, [trackWidth]);

  const trackResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onMoveShouldSetPanResponder: () => true,
    onPanResponderGrant: (evt) => {
      setIsDragging(true);
      updateDragFromX(evt.nativeEvent.locationX);
    },
    onPanResponderMove: (evt) => {
      updateDragFromX(evt.nativeEvent.locationX);
    },
    onPanResponderRelease: () => {
      const ratio = dragRatioRef.current;
      setIsDragging(false);
      setDragRatio(null);
      dragRatioRef.current = null;
      if (typeof ratio === 'number') seekToRatio(ratio);
    },
    onPanResponderTerminate: () => {
      const ratio = dragRatioRef.current;
      setIsDragging(false);
      setDragRatio(null);
      dragRatioRef.current = null;
      if (typeof ratio === 'number') seekToRatio(ratio);
    },
  }), [updateDragFromX, seekToRatio]);

  const progressRatio = durationMillis > 0
    ? Math.max(0, Math.min(1, positionMillis / durationMillis))
    : 0;
  const displayRatio = isDragging && typeof dragRatio === 'number' ? dragRatio : progressRatio;
  const displayPosMs = isDragging && typeof dragRatio === 'number' && durationMillis
    ? Math.round(durationMillis * dragRatio)
    : positionMillis;

  return (
    <View style={mediaStyles.musicFullWrap}>
      <MusicBlobBackground gradient={gradient} playing={playing} />

      {item.thumbnailPath && (
        <Image source={{ uri: item.thumbnailPath }} style={mediaStyles.musicBgImage} contentFit="cover" blurRadius={40} />
      )}

      <View style={mediaStyles.musicContent}>
        {item.thumbnailPath ? (
          <Image source={{ uri: item.thumbnailPath }} style={mediaStyles.musicArt} contentFit="cover" />
        ) : (
          // No cover art → fall back to the same gradient driving the orbs
          // so the artwork tile reads as part of the same color story.
          <LinearGradient
            colors={gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[mediaStyles.musicArt, { justifyContent: 'center', alignItems: 'center' }]}
          >
            <Icon name="music" size={48} color={fgIcon} solid />
          </LinearGradient>
        )}

        <Text size="l" weight="semiBold" style={{ color: fgPrimary, textAlign: 'center' }} numberOfLines={2}>
          {item.displayName || item.prompt}
        </Text>

        <Text size="s" style={{ color: fgSecondary, textAlign: 'center' }} numberOfLines={1}>
          {item.modelName || item.modelSlug}
        </Text>

        <View style={mediaStyles.waveformRow}>
          {[...Array(NUM_BARS)].map((_, i) => (
            <LightboxWaveBar key={i} index={i} itemId={item.id} playing={playing} />
          ))}
        </View>

        <View style={mediaStyles.seekRow}>
          <Text size="xs" style={mediaStyles.timeLabel}>{formatMillis(displayPosMs)}</Text>
          <View style={mediaStyles.trackWrapper}>
            <View
              style={mediaStyles.trackBg}
              onLayout={(e) => setTrackWidth(Math.round(e.nativeEvent.layout.width))}
            >
              <View style={[mediaStyles.trackFill, { width: `${displayRatio * 100}%` }]} />
              {!!trackWidth && (
                <View
                  style={[
                    mediaStyles.trackKnob,
                    { left: Math.max(0, Math.min(trackWidth - 14, (displayRatio * trackWidth) - 7)) },
                  ]}
                  pointerEvents="none"
                />
              )}
              <View
                style={StyleSheet.absoluteFill}
                {...trackResponder.panHandlers}
                onMouseEnter={() => { if (Platform.OS === 'web') setIsHovering(true); }}
                onMouseLeave={() => { if (Platform.OS === 'web') { setIsHovering(false); setHoverRatio(null); } }}
                onMouseMove={(e) => {
                  if (Platform.OS !== 'web') return;
                  try {
                    const rect = e.currentTarget.getBoundingClientRect?.();
                    const x = typeof e.clientX === 'number' && rect ? (e.clientX - rect.left) : 0;
                    const w = rect?.width || trackWidth || 0;
                    if (w > 0) setHoverRatio(Math.max(0, Math.min(1, x / w)));
                  } catch {}
                }}
              />
              {Platform.OS === 'web' && isHovering && !isDragging && typeof hoverRatio === 'number' && !!trackWidth && (
                <View
                  style={[
                    mediaStyles.trackGhostKnob,
                    { left: Math.max(0, Math.min(trackWidth - 14, (hoverRatio * trackWidth) - 7)) },
                  ]}
                  pointerEvents="none"
                />
              )}
            </View>
          </View>
          <Text size="xs" style={mediaStyles.timeLabel}>{formatMillis(durationMillis)}</Text>
        </View>

        <Pressable onPress={toggle} style={mediaStyles.musicPlayBtnLarge}>
          <Icon name={playing ? 'pause' : 'play'} size={26} color="#fff" solid />
        </Pressable>
      </View>
    </View>
  );
}

export function LightboxMedia({ item, colors }) {
  if (item.mediaType === 'image') return <LightboxImage item={item} />;
  if (item.mediaType === 'video') return <LightboxVideo item={item} />;
  if (item.mediaType === 'music') return <LightboxMusic item={item} colors={colors} />;
  if (item.mediaType === '3d') return <Lightbox3D item={item} />;
  return null;
}

function Lightbox3D({ item }) {
  const alternateFormats = useMemo(() => (
    item.metadata?.['3d']?.alternateFormats
      || item.metadata?.threed?.alternateFormats
      || []
  ), [item]);

  const usdzSrc = useMemo(() => {
    const usdz = alternateFormats.find(f => (f.format || '').toLowerCase() === 'usdz');
    return usdz?.url || null;
  }, [alternateFormats]);

  const inferredFormat = useMemo(() => {
    const meta = item.metadata?.['3d'] || item.metadata?.threed || {};
    if (meta.format) return String(meta.format).toLowerCase();
    const u = item.cdnPath || '';
    const cleaned = u.split('?')[0].split('#')[0];
    return cleaned.split('.').pop()?.toLowerCase() || null;
  }, [item]);

  return (
    <View style={mediaStyles.threeDWrap}>
      <Model3DViewer
        modelUrl={item.cdnPath}
        posterUrl={item.thumbnailPath}
        iosSrc={usdzSrc}
        alternateFormats={alternateFormats}
        autoRotate
        style={mediaStyles.threeDViewer}
      />
      {inferredFormat && (
        <View style={mediaStyles.threeDFormatBadge} pointerEvents="none">
          <Icon name="cube" size={11} color="#fff" solid />
          <Text size="xxs" weight="semiBold" color="white" style={{ marginLeft: 4 }}>
            {inferredFormat.toUpperCase()}
          </Text>
        </View>
      )}
    </View>
  );
}

const mediaStyles = StyleSheet.create({
  imageWrap: { flex: 1, justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' },
  image: { width: '100%', maxWidth: '100%', maxHeight: '100%' },
  videoWrap: { width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', borderRadius: 8, overflow: 'hidden' },
  videoThumb: { width: '100%', maxWidth: '100%', maxHeight: '100%' },
  videoThumbFill: { width: '100%', height: '100%' },
  videoView: { width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' },
  videoNativeView: { width: '100%', height: '100%', backgroundColor: '#000' },
  threeDWrap: {
    flex: 1,
    alignSelf: 'stretch',
    width: '100%',
    height: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  threeDViewer: { flex: 1, width: '100%', height: '100%' },
  threeDFormatBadge: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.55)',
  },
  playOverlay: { ...StyleSheet.absoluteFillObject, justifyContent: 'center', alignItems: 'center' },
  playCircle: { width: 64, height: 64, borderRadius: 32, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', paddingLeft: 3 },
  musicFullWrap: { flex: 1, width: '100%', borderRadius: 10, overflow: 'hidden', height: '100%', justifyContent: 'center', alignItems: 'center' },
  musicBgImage: { ...StyleSheet.absoluteFillObject, opacity: 0.3 },
  musicContent: { alignItems: 'center', gap: 16, paddingHorizontal: 32, maxWidth: 400, zIndex: 1 },
  musicArt: { width: 180, height: 180, borderRadius: 20, ...(isWeb && { boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }) },
  waveformRow: { flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 64, width: '100%', paddingHorizontal: 8 },
  musicPlayBtnLarge: { width: 60, height: 60, borderRadius: 30, backgroundColor: 'rgba(0,0,0,0.35)', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: 'rgba(255,255,255,0.25)', marginTop: 4 },
  seekRow: { flexDirection: 'row', alignItems: 'center', gap: 10, width: '100%', paddingHorizontal: 4, marginTop: -4 },
  timeLabel: { color: 'rgba(255,255,255,0.85)', minWidth: 36, textAlign: 'center', fontVariant: ['tabular-nums'] },
  trackWrapper: { flex: 1, height: 16, justifyContent: 'center' },
  trackBg: { width: '100%', height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.22)', position: 'relative' },
  trackFill: { height: '100%', borderRadius: 3, backgroundColor: '#fff' },
  trackKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#fff',
    ...Platform.select({
      web: { boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.35)' },
      default: { shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.35, shadowRadius: 2, elevation: 2 },
    }),
  },
  trackGhostKnob: {
    position: 'absolute',
    top: -4,
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: 'transparent',
    borderWidth: 2,
    borderColor: '#fff',
    opacity: 0.6,
  },
});

// ── Sticky Prompt Input ─────────────────────────────────────────────────────

const MEDIA_TYPES = [
  { id: 'image', icon: 'image', label: 'Image' },
  { id: 'video', icon: 'video', label: 'Video' },
  { id: 'music', icon: 'music', label: 'Music' },
  { id: '3d', icon: 'cube', label: '3D' },
];

const MIN_INPUT_H = 40;
const MAX_INPUT_H = 140;

function getWebTextAreaEl(ref) {
  const r = ref?.current;
  if (!r) return null;
  if (typeof window !== 'undefined' && r instanceof window.HTMLTextAreaElement) return r;
  if (r._node?.firstChild && r._node.firstChild.tagName === 'TEXTAREA') return r._node.firstChild;
  if (r._inputElement) return r._inputElement;
  const q = r.querySelector?.('textarea');
  return q || null;
}

const GENERATE_LABELS = { image: 'Generate Image', video: 'Generate Video', music: 'Generate Music', '3d': 'Generate 3D' };
const GENERATE_ICONS = { image: 'image', video: 'video', music: 'music', '3d': 'cube' };
// Vertical chrome around the textarea inside the remix panel (controls row +
// padding + borders). Used to derive the panel height from the current input
// height so the send button never gets pushed out of view as the user types.
const REMIX_PANEL_CHROME = 48;

export function StickyPromptInput({ remixInput, setRemixInput, mediaType, setMediaType, onRemix, onUsePrompt, isSubmitting, colors, itemMediaType, clones }) {
  const inputRef = useRef(null);
  const [inputHeight, setInputHeight] = useState(MIN_INPUT_H);
  const [remixOpen, setRemixOpen] = useState(false);
  const expandAnim = useRef(new Animated.Value(0)).current;

  const syncH = useCallback(() => {
    if (!isWeb) return;
    const el = getWebTextAreaEl(inputRef);
    if (!el || typeof el.scrollHeight !== 'number') return;
    el.style.height = `${MIN_INPUT_H}px`;
    const next = Math.min(Math.max(MIN_INPUT_H, el.scrollHeight), MAX_INPUT_H);
    el.style.height = `${next}px`;
    setInputHeight(next);
  }, []);

  useLayoutEffect(() => { syncH(); }, [remixInput, syncH]);

  const toggleRemix = useCallback(() => {
    const opening = !remixOpen;
    setRemixOpen(opening);
    Animated.spring(expandAnim, {
      toValue: opening ? 1 : 0,
      useNativeDriver: false,
      speed: 22,
      bounciness: 0,
    }).start();
    if (opening) setTimeout(() => inputRef.current?.focus(), 160);
  }, [remixOpen, expandAnim]);

  const handleWebKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (remixInput.trim()) onRemix();
    else onUsePrompt();
  }, [onRemix, onUsePrompt, remixInput]);

  // Close remix panel when user submits
  const handleCreate = useCallback(() => {
    onUsePrompt();
  }, [onUsePrompt]);

  const hasRemixText = !!remixInput.trim();
  const isMusic = itemMediaType === 'music';
  const isVideo = itemMediaType === 'video';
  const is3D = itemMediaType === '3d';
  const visibleMediaTypes = is3D
    ? MEDIA_TYPES.filter(mt => mt.id === '3d')
    : isMusic
      ? MEDIA_TYPES.filter(mt => mt.id !== '3d')
      : isVideo
        ? MEDIA_TYPES.filter(mt => mt.id !== 'image' && mt.id !== 'music' && mt.id !== '3d')
        : MEDIA_TYPES.filter(mt => mt.id !== 'music' && mt.id !== '3d');
  // Reflect the ACTIVE OUTPUT TYPE tab (image / video / music / 3d) in the
  // CTA label & icon, not the source item's media type — when the user
  // toggles "video" on an image source, the button should say "Generate Video"
  // because that's what's actually about to happen.
  const generateLabel = GENERATE_LABELS[mediaType] || GENERATE_LABELS[itemMediaType] || 'Create this';
  const generateIcon = GENERATE_ICONS[mediaType] || GENERATE_ICONS[itemMediaType] || 'wand-magic-sparkles';

  const expandedPanelHeight = inputHeight + REMIX_PANEL_CHROME;
  const remixHeight = expandAnim.interpolate({ inputRange: [0, 1], outputRange: [0, expandedPanelHeight] });
  const remixOpacity = expandAnim.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, 0, 1] });

  return (
    <View style={[stickyStyles.container, { borderTopColor: colors.border_color_secondary }]}>

      {/* Social proof */}
      {clones > 0 && (
        <View style={stickyStyles.socialProof}>
          <Icon name="users" size={10} color="#8b5cf6" />
          <Text size="xxs" style={{ color: '#8b5cf6', letterSpacing: 0.1 }}>
            {clones.toLocaleString()} {clones === 1 ? 'creator' : 'creators'} made this
          </Text>
        </View>
      )}

      {/* Primary CTA + Remix toggle row */}
      <View style={stickyStyles.ctaRow}>
        <Pressable
          onPress={handleCreate}
          disabled={isSubmitting}
          style={({ pressed }) => [stickyStyles.generateBtnWrapper, { opacity: isSubmitting ? 0.7 : pressed ? 0.9 : 1 }]}
        >
          <LinearGradient
            colors={['#7c3aed', '#4338ca']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={stickyStyles.generateBtn}
          >
            <Icon
              name={isSubmitting ? 'circle-notch' : generateIcon}
              size={14}
              color="#fff"
              solid
            />
            <Text size="s" weight="bold" style={stickyStyles.generateBtnText}>
              {isSubmitting ? 'Creating…' : generateLabel}
            </Text>
          </LinearGradient>
        </Pressable>

        {!isMusic && (
          <Pressable
            onPress={toggleRemix}
            style={[stickyStyles.remixToggle, { borderColor: remixOpen ? '#7c3aed' : colors.border_color_secondary, backgroundColor: remixOpen ? 'rgba(124,58,237,0.08)' : colors.solid_color_secondary }]}
          >
            <Icon name={remixOpen ? 'xmark' : 'sliders'} size={13} color={remixOpen ? '#7c3aed' : colors.text_color_secondary} />
            {!remixOpen && (
              <Text size="xs" weight="medium" color='secondary'>Remix</Text>
            )}
          </Pressable>
        )}
      </View>

      {/* Expandable remix input */}
      {!isMusic && (
        <Animated.View style={{ height: remixHeight, overflow: 'hidden' }}>
          <Animated.View style={{ opacity: remixOpacity }}>
            <View style={[stickyStyles.inputBox, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary }]}>
              <View style={[stickyStyles.inputRow, isWeb && { height: inputHeight }]}>
                <TextInput
                  ref={inputRef}
                  value={remixInput}
                  onChangeText={setRemixInput}
                  placeholder="Describe your changes…"
                  placeholderTextColor={colors.color_input_placeholder || colors.text_color_secondary}
                  style={[stickyStyles.textarea, { color: colors.text_color_primary, ...(isWeb ? { height: inputHeight } : { maxHeight: MAX_INPUT_H }) }]}
                  multiline
                  scrollEnabled={!isWeb}
                  textAlignVertical="top"
                  returnKeyType="default"
                  blurOnSubmit={false}
                  {...(isWeb ? { onKeyDown: handleWebKeyDown } : {})}
                />
              </View>
              <View style={stickyStyles.controlsRow}>
                <View style={stickyStyles.mediaTypeRow}>
                  {visibleMediaTypes.map(mt => {
                    const isActive = mediaType === mt.id;
                    return (
                      <Pressable
                        key={mt.id}
                        onPress={() => setMediaType(mt.id)}
                        style={[stickyStyles.mediaTypeBtn, { backgroundColor: isActive ? colors.solid_color_secondary_dark : 'transparent', borderColor: isActive ? colors.border_color_secondary : 'transparent' }]}
                      >
                        <Icon name={mt.icon} size={11} color={isActive ? colors.text_color_primary : colors.text_color_secondary} />
                        <Text size="xxs" weight={isActive ? 'medium' : 'regular'} style={{ color: isActive ? colors.text_color_primary : colors.text_color_secondary }}>{mt.label}</Text>
                      </Pressable>
                    );
                  })}
                </View>
                <ChatInputSendButton onPress={onRemix} isDisabled={!hasRemixText || isSubmitting} />
              </View>
            </View>
          </Animated.View>
        </Animated.View>
      )}
    </View>
  );
}

const stickyStyles = StyleSheet.create({
  container: { paddingHorizontal: 14, paddingTop: 10, paddingBottom: 14, borderTopWidth: 1, gap: 8 },
  socialProof: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 5,
    paddingVertical: 2,
  },
  ctaRow: { flexDirection: 'row', gap: 8, alignItems: 'stretch' },
  generateBtnWrapper: {
    flex: 1,
    borderRadius: 13,
    overflow: 'hidden',
    ...(isWeb && { cursor: 'pointer' }),
  },
  generateBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    gap: 8, paddingVertical: 14,
  },
  generateBtnText: { color: '#fff', letterSpacing: 0.1 },
  remixToggle: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 10,
    borderRadius: 13, borderWidth: 1,
    ...(isWeb && { cursor: 'pointer', transition: 'all 0.15s' }),
  },
  inputBox: { borderRadius: 14, borderWidth: 1, overflow: 'hidden' },
  inputRow: { flexDirection: 'row', alignItems: 'flex-start', minHeight: MIN_INPUT_H, maxHeight: MAX_INPUT_H },
  textarea: {
    flex: 1, width: '100%', backgroundColor: 'transparent', paddingVertical: 10, paddingHorizontal: 14,
    fontSize: 14, lineHeight: 20, minHeight: MIN_INPUT_H, borderWidth: 0,
    ...(isWeb && { outlineStyle: 'none', border: 'none', resize: 'none', WebkitAppearance: 'none' }),
  },
  controlsRow: { paddingHorizontal: 6, paddingBottom: 6, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  mediaTypeRow: { flexDirection: 'row', gap: 1 },
  mediaTypeBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 4, borderRadius: 6, borderWidth: 1 },
});

// ── Detail Panel Header ─────────────────────────────────────────────────────
// Sticky header rendered above the panel ScrollView. Holds title + model/stat
// chips. Picks up a hairline bottom border once the scroll view has moved.

export const DetailPanelHeader = React.memo(({
  resolvedItem, colors, scrolled, onModelPress,
  isCompact = false, isExpanded = false, onToggleExpand,
}) => {
  if (!resolvedItem) return null;
  const isPlusShowcase = resolvedItem.flags?.isPlusShowcase;
  const showStats = !isCompact || isExpanded;

  // On compact, tapping anywhere in the slim peek header expands the sheet.
  // When already expanded, only the chevron collapses (so taps on the title /
  // model pill don't accidentally dismiss the user's reading position).
  const headerPressable = isCompact && !isExpanded;

  const HeaderWrapper = headerPressable ? Pressable : View;
  const wrapperProps = headerPressable
    ? { onPress: onToggleExpand, accessibilityRole: 'button', accessibilityLabel: 'Expand details' }
    : {};

  return (
    <HeaderWrapper
      {...wrapperProps}
      style={[
        panelStyles.header,
        isCompact && panelStyles.headerCompact,
        scrolled && { borderBottomColor: colors.border_color_secondary },
      ]}
    >
      {/* Sheet drag-handle affordance — only on compact, signals the panel is grabbable */}
      {isCompact && (
        <View style={panelStyles.dragHandleContainer} pointerEvents="none">
          <View style={[panelStyles.dragHandle, { backgroundColor: colors.text_color_tertiary }]} />
        </View>
      )}

      <View style={panelStyles.headerRow}>
        <View style={{ flex: 1, gap: 8 }}>
          {resolvedItem.displayName && (
            <Text
              size="l"
              weight="bold"
              color="primary"
              style={{ lineHeight: 26 }}
              numberOfLines={isCompact && !isExpanded ? 1 : 2}
            >
              {resolvedItem.displayName}
            </Text>
          )}
          <View style={panelStyles.metaRow}>
            {resolvedItem.modelName && (
              <Pressable onPress={() => onModelPress?.(resolvedItem.modelSlug)} style={[panelStyles.modelPill, { backgroundColor: colors.solid_color_tertiary }]}>
                <Icon name="microchip" size={10} color={colors.text_color_secondary} />
                <Text size="xxs" weight="medium" color="secondary">{resolvedItem.modelName}</Text>
              </Pressable>
            )}
            {isPlusShowcase && (
              <View style={panelStyles.plusPill}>
                <Icon name="bolt" size={9} color="#fff" solid />
                <Text size="xxs" weight="bold" style={{ color: '#fff' }}>Plus</Text>
              </View>
            )}
            <View style={{ flex: 1 }} />
            {showStats && resolvedItem.stats?.views > 0 && (
              <View style={panelStyles.statChip}>
                <Icon name="eye" size={10} color={colors.text_color_tertiary} />
                <Text size="xxs" color="tertiary">{resolvedItem.stats.views.toLocaleString()}</Text>
              </View>
            )}
            {showStats && resolvedItem.stats?.clones > 0 && (
              <View style={panelStyles.statChip}>
                <Icon name="wand-magic-sparkles" size={10} color={colors.text_color_tertiary} />
                <Text size="xxs" color="tertiary">{resolvedItem.stats.clones.toLocaleString()}</Text>
              </View>
            )}
          </View>
        </View>

        {isCompact && (
          <Pressable
            onPress={onToggleExpand}
            style={[panelStyles.expandToggle, { backgroundColor: colors.solid_color_tertiary }]}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            <Icon name={isExpanded ? 'chevron-down' : 'chevron-up'} size={13} color={colors.text_color_secondary} />
          </Pressable>
        )}
      </View>
    </HeaderWrapper>
  );
});

DetailPanelHeader.displayName = 'DetailPanelHeader';

// ── Copy chip ──────────────────────────────────────────────────────────────
// Tiny inline button used next to field labels (negative prompt, music
// description, lyrics, etc.). Self-contained: owns its own copied/timer state,
// fires a toast + haptic, and resets the "Copied!" label after 2.2s. Returns
// null when there's no text to copy so callers can drop it in unconditionally.
const CopyChip = React.memo(({ text, label = 'Text', analyticsKey, itemId, colors }) => {
  const addToast = useToastStore(s => s.actions.addToast);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const handleCopy = useCallback(async () => {
    if (!text || copied) return;
    if (analyticsKey && itemId) trackEvent('explore', analyticsKey, itemId);
    try {
      await copyText(text);
      mediumTap();
      addToast(`${label} copied`, 'success');
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2200);
    } catch {
      addToast('Failed to copy', 'error');
    }
  }, [text, copied, label, analyticsKey, itemId, addToast]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  if (!text) return null;

  return (
    <Pressable
      onPress={handleCopy}
      style={[panelStyles.copyBtn, { backgroundColor: colors.solid_color_tertiary }]}
      hitSlop={6}
      accessibilityRole="button"
      accessibilityLabel={`Copy ${label.toLowerCase()}`}
    >
      {copied ? (
        <Text size="xxs" weight="semiBold" color="secondary">Copied!</Text>
      ) : (
        <Icon name="copy" size={11} color={colors.text_color_secondary} />
      )}
    </Pressable>
  );
});

CopyChip.displayName = 'CopyChip';

// ── Aspect-aware image ─────────────────────────────────────────────────────
// Renders an image at its native aspect ratio with a blurhash background
// behind it so any letterboxing reads as the image's own dominant colors
// instead of as transparent / black. The aspect ratio is sourced in this
// priority: explicit `aspectRatio` prop → `onLoad` intrinsic dimensions →
// fallback (defaults to 1:1 until measured). The blurhash both seeds the
// loading state via expo-image's placeholder *and* sits as a separately
// absolutely-positioned layer behind the image so it stays visible after
// load when `contentFit="contain"` produces letterbox bars.
const AspectImage = React.memo(({ uri, blurhash, aspectRatio, fallbackAspect = 1, borderRadius = 10, backgroundColor, contentFit = 'contain', maxHeight }) => {
  const [intrinsicAspect, setIntrinsicAspect] = useState(null);
  const ratio = aspectRatio || intrinsicAspect || fallbackAspect;

  const handleLoad = useCallback((e) => {
    const w = e?.source?.width;
    const h = e?.source?.height;
    if (w && h) setIntrinsicAspect(w / h);
  }, []);

  // The container is always full width; `aspectRatio` sets the natural
  // height (= width / ratio) and `maxHeight` clamps it. For portrait images
  // the clamp wins, producing a wider-than-tall frame where the blurhash
  // fills the full panel width and the image sits `contain`-fitted in the
  // middle with blurhash bars showing through on either side.

  return (
    <View
      style={{
        width: '100%',
        aspectRatio: ratio,
        ...(maxHeight != null ? { maxHeight } : null),
        borderRadius,
        overflow: 'hidden',
        backgroundColor,
      }}
    >
      {blurhash ? (
        <Image
          source={{ blurhash }}
          style={StyleSheet.absoluteFillObject}
          contentFit="cover"
          pointerEvents="none"
        />
      ) : null}
      <Image
        source={{ uri }}
        style={StyleSheet.absoluteFillObject}
        contentFit={contentFit}
        placeholder={blurhash ? { blurhash } : undefined}
        transition={150}
        onLoad={aspectRatio ? undefined : handleLoad}
      />
    </View>
  );
});
AspectImage.displayName = 'AspectImage';

// ── Derivation helpers ─────────────────────────────────────────────────────
// `derivationType` is a free-form string on the schema. These helpers convert
// it into a friendly label + an icon name from the design system. Centralised
// so the small "from" badge, the rich "Derived from" card, and the parent's
// "Variations" carousel all stay in lockstep.
const DERIVATION_META = {
  'image-to-image':   { label: 'Style variation',     icon: 'image',         shortLabel: 'Style' },
  'image-to-video':   { label: 'Animated from image', icon: 'video',         shortLabel: 'Video' },
  'image-to-3d':      { label: '3D from image',       icon: 'cube',          shortLabel: '3D' },
  'video-to-video':   { label: 'Video variation',     icon: 'video',         shortLabel: 'Video' },
  'music-variation':  { label: 'Music variation',     icon: 'music',         shortLabel: 'Music' },
  'prompt-variation': { label: 'Prompt variation',    icon: 'wand-magic-sparkles', shortLabel: 'Prompt' },
  'cross-media':      { label: 'Cross-media remix',   icon: 'shuffle',       shortLabel: 'Remix' },
};

function getDerivationMeta(type) {
  return DERIVATION_META[type] || { label: 'Derived', icon: 'code-branch', shortLabel: 'Derived' };
}

// ── Derived From Card ─────────────────────────────────────────────────────
// Rich provenance card shown when the current item descends from another
// explore item. Tapping it navigates the lightbox to the parent so users can
// trace a variation chain back to its source. Replaces the prior tiny
// text-only "Generated from image" badge with something that actually shows
// the parent (thumbnail + prompt) — much higher signal at the same vertical
// cost in the panel.
const DerivedFromCard = React.memo(({ parent, derivationType, colors, onPress }) => {
  if (!parent) return null;
  const meta = getDerivationMeta(derivationType);
  const thumb = parent.thumbnailPath || parent.cdnPath;
  const title = parent.displayName || parent.prompt || 'Original';

  return (
    <View style={{ gap: 6 }}>
      <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>DERIVED FROM</Text>
      <Pressable
        onPress={() => onPress?.(parent)}
        style={[
          derivationStyles.derivedCard,
          { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary },
        ]}
        accessibilityRole="button"
        accessibilityLabel={`View original: ${title}`}
      >
        {thumb ? (
          <Image source={{ uri: thumb }} style={derivationStyles.derivedThumb} contentFit="cover" />
        ) : (
          <View style={[derivationStyles.derivedThumb, { backgroundColor: colors.solid_color_tertiary, alignItems: 'center', justifyContent: 'center' }]}>
            <Icon name={parent.mediaType === 'video' ? 'video' : parent.mediaType === 'music' ? 'music' : parent.mediaType === '3d' ? 'cube' : 'image'} size={18} color={colors.text_color_tertiary} />
          </View>
        )}
        <View style={{ flex: 1, gap: 4 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
            <Icon name={meta.icon} size={10} color={colors.text_color_tertiary} />
            <Text size="xxs" weight="medium" color="tertiary" numberOfLines={1}>{meta.label}</Text>
          </View>
          <Text size="xs" color="primary" numberOfLines={2} style={{ lineHeight: 16 }}>{title}</Text>
        </View>
        <Icon name="arrow-up-right-from-square" size={10} color={colors.text_color_tertiary} />
      </Pressable>
    </View>
  );
});
DerivedFromCard.displayName = 'DerivedFromCard';

// ── Variations Carousel ───────────────────────────────────────────────────
// Horizontal strip of every active child of the current item, grouped by
// derivation type so users can scan "what else was made from this one".
// Each thumbnail is a tap target that swaps the lightbox to the variation —
// the variation chain becomes a first-class navigation surface, not just a
// hidden join in the schema.
export const VariationsSection = React.memo(({
  variations,
  colors,
  onSelect,
  /** Default 84 — item page uses 200 for larger tap targets. */
  tileSize = 84,
  /** When set, replaces the compact all-caps title row styling (item page). */
  titleTextProps = null,
}) => {
  const { t } = useTranslation();
  const safeVariations = useMemo(() => (
    Array.isArray(variations) ? variations : []
  ), [variations]);

  const badgeScale = tileSize / 84;

  // Group + count by derivation type for the header summary chip(s). This
  // gives users an at-a-glance sense of *what kinds* of variations exist
  // ("3 style · 2 video") before they scroll the strip.
  const groupedCounts = useMemo(() => {
    const counts = {};
    for (const v of safeVariations) {
      const key = v.derivationType || 'other';
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts);
  }, [safeVariations]);

  if (safeVariations.length === 0) return null;

  const titleRow = titleTextProps ? (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <Icon name="code-branch" size={12} color={colors.text_color_tertiary} />
      <Text {...titleTextProps}>
        {t('explore.variationsLabel')} · {safeVariations.length}
      </Text>
    </View>
  ) : (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
      <Icon name="code-branch" size={11} color={colors.text_color_tertiary} />
      <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>
        VARIATIONS · {safeVariations.length}
      </Text>
    </View>
  );

  return (
    <View style={{ gap: 8 }}>
      <View style={panelStyles.fieldHeaderRow}>
        {titleRow}
        <View style={{ flexDirection: 'row', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {groupedCounts.map(([type, count]) => {
            const meta = getDerivationMeta(type);
            return (
              <View key={type} style={[derivationStyles.typeCountChip, { backgroundColor: colors.solid_color_tertiary }]}>
                <Icon name={meta.icon} size={9} color={colors.text_color_secondary} />
                <Text size="xxs" color="secondary">{count}</Text>
              </View>
            );
          })}
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ gap: 8, paddingRight: 8 }}
      >
        {safeVariations.map((v) => {
          const meta = getDerivationMeta(v.derivationType);
          const thumb = v.thumbnailPath || v.cdnPath;
          const badgeBox = {
            width: Math.round(18 * badgeScale),
            height: Math.round(18 * badgeScale),
            borderRadius: Math.round(9 * badgeScale),
            top: Math.max(4, Math.round(4 * badgeScale)),
            right: Math.max(4, Math.round(4 * badgeScale)),
          };
          const pipBox = {
            width: Math.round(18 * badgeScale),
            height: Math.round(18 * badgeScale),
            borderRadius: Math.round(9 * badgeScale),
            bottom: Math.max(4, Math.round(4 * badgeScale)),
            left: Math.max(4, Math.round(4 * badgeScale)),
          };
          const iconFallback = Math.min(28, Math.max(20, Math.round(20 * badgeScale)));
          const pipIcon = Math.min(12, Math.max(10, Math.round(10 * badgeScale)));
          const typeIcon = Math.min(11, Math.max(9, Math.round(9 * badgeScale)));
          return (
            <Pressable
              key={v.id}
              onPress={() => onSelect?.(v)}
              style={[
                derivationStyles.variationTile,
                {
                  width: tileSize,
                  height: tileSize,
                  borderRadius: Math.min(14, Math.round(10 + (tileSize - 84) * 0.05)),
                  borderColor: colors.border_color_secondary,
                  backgroundColor: colors.solid_color_secondary,
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Open variation: ${meta.label}`}
            >
              {thumb ? (
                <Image source={{ uri: thumb }} style={derivationStyles.variationThumb} contentFit="cover" />
              ) : (
                <View style={[derivationStyles.variationThumb, { alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_tertiary }]}>
                  <Icon name={v.mediaType === 'video' ? 'video' : v.mediaType === 'music' ? 'music' : v.mediaType === '3d' ? 'cube' : 'image'} size={iconFallback} color={colors.text_color_tertiary} />
                </View>
              )}
              {/* Type badge — overlaid top-right to keep the thumbnail clean */}
              <View style={[derivationStyles.variationTypeBadge, badgeBox]}>
                <Icon name={meta.icon} size={typeIcon} color="#fff" />
              </View>
              {/* Media-type pip for non-image children (so video/3d are obvious) */}
              {v.mediaType !== 'image' && (
                <View style={[derivationStyles.variationMediaPip, pipBox]}>
                  <Icon
                    name={v.mediaType === 'video' ? 'play' : v.mediaType === 'music' ? 'music' : v.mediaType === '3d' ? 'cube' : 'image'}
                    size={pipIcon}
                    color="#fff"
                    solid
                  />
                </View>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
});
VariationsSection.displayName = 'VariationsSection';

const derivationStyles = StyleSheet.create({
  derivedCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    padding: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  derivedThumb: {
    width: 56,
    height: 56,
    borderRadius: 8,
    overflow: 'hidden',
  },
  typeCountChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 5,
  },
  variationTile: {
    width: 84,
    height: 84,
    borderRadius: 10,
    borderWidth: 1,
    overflow: 'hidden',
    ...(isWeb && { cursor: 'pointer' }),
  },
  variationThumb: {
    width: '100%',
    height: '100%',
  },
  variationTypeBadge: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  variationMediaPip: {
    position: 'absolute',
    bottom: 4,
    left: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(0,0,0,0.65)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// ── Anonymous-user signup CTA ───────────────────────────────────────────────
// Shown ONLY at the very top of the lightbox detail panel for fully
// anonymous visitors (no token at all). Ghost users already have an
// account in our funnel so we don't show this to them — they continue to
// see the regular action buttons which already prompt them via
// GhostSignupModal on intent.
//
// Visual: vivid gradient card so it reads as the top of the panel before
// the user ever scrolls; primary "Sign up" + secondary text "Log in" so
// returning users have a fast path back in.
const LightboxSignupCTA = React.memo(({ mediaType }) => {
  const openModal = useModalStore(s => s.actions.openModal);

  const verb = mediaType === 'video' ? 'video'
    : mediaType === 'music' ? 'song'
    : mediaType === '3d' ? '3D model'
    : 'image';

  const handleSignup = useCallback(() => {
    trackEvent('explore', 'explore_lightbox_signup_click', mediaType);
    openModal('SignupModal');
  }, [openModal, mediaType]);

  const handleLogin = useCallback(() => {
    trackEvent('explore', 'explore_lightbox_login_click', mediaType);
    openModal('LoginModal');
  }, [openModal, mediaType]);

  return (
    <View style={ctaStyles.outer}>
      <LinearGradient
        colors={['#C2410C', '#7C2D12']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={ctaStyles.gradient}
      >
        <View style={ctaStyles.iconBubble}>
          <Icon name="wand-magic-sparkles" size={16} color="#fff" />
        </View>
        <View style={{ flex: 1, gap: 2 }}>
          <Text size="m" weight="bold" style={{ color: '#fff' }}>
            Make your own {verb}
          </Text>
          <Text size="xs" style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 16 }}>
            Sign up free to remix this, save favorites, and start creating with AI.
          </Text>
        </View>
      </LinearGradient>

      <View style={ctaStyles.actionRow}>
        <Pressable
          onPress={handleSignup}
          style={({ pressed }) => [ctaStyles.signupBtn, { opacity: pressed ? 0.9 : 1 }]}
        >
          <Icon name="user-plus" size={12} color="#fff" />
          <Text size="s" weight="semiBold" style={{ color: '#fff' }}>Sign up free</Text>
        </Pressable>
        <Pressable
          onPress={handleLogin}
          style={({ pressed }) => [ctaStyles.loginBtn, { opacity: pressed ? 0.7 : 1 }]}
        >
          <Text size="s" weight="medium" style={{ color: '#fff' }}>Log in</Text>
        </Pressable>
      </View>
    </View>
  );
});

LightboxSignupCTA.displayName = 'LightboxSignupCTA';

const ctaStyles = StyleSheet.create({
  outer: {
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: '#7C2D12',
    marginBottom: 4,
  },
  gradient: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    paddingBottom: 12,
  },
  iconBubble: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  signupBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    height: 38,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
    ...(isWeb ? { cursor: 'pointer', transition: 'opacity 0.15s' } : {}),
  },
  loginBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
    height: 38,
    borderRadius: 10,
    ...(isWeb ? { cursor: 'pointer' } : {}),
  },
});

// ── Detail Panel Content ────────────────────────────────────────────────────
// Extracted so it can crossfade without remounting the outer panel shell

export const DetailPanelContent = React.memo(({
  resolvedItem, colors, activeMediaType, setActiveMediaType,
  modelSlug, setModelSlug, localParams, setLocalParams, musicDescription, setMusicDescription,
  musicLyrics, setMusicLyrics, lyricsExpanded, setLyricsExpanded,
  handleCopyPrompt, isPlus, onCategoryPress, onDownload, modelSelectorInputs,
  /** When `page`, parent/child links use the router (`/explore/item/...`). Default `lightbox` keeps in-overlay navigation. */
  relatedNavMode = 'lightbox',
  /** Standalone item page renders variations under the media column instead. */
  hideVariationsSection = false,
}) => {
  const { openModal } = useModalStore(s => s.actions);
  const navigateLightboxTo = useExploreLightboxStore(s => s.actions.navigateTo);
  const fetchExploreItem = useExploreStore(s => s.actions.fetchItem);
  const router = useRouter();
  // Anonymous = no token at all. Ghost users have a token, so they don't see
  // the CTA card here (their action-button taps already trigger the
  // GhostSignupModal flow elsewhere).
  const isAnon = useAuthStore(s => !s.token);
  const [model, setModel] = useState(() => modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null);
  const fetchedSlugsRef = useRef({});

  // Handler for navigating between an item and its parent or any of its
  // children inside the same lightbox session. We use the partial item we
  // already have (parent / variation thumbnails come pre-serialized from the
  // detail endpoint) so the swap is instant, then kick off the full detail
  // fetch in the background — `decorateExploreItem` upgrades the cached entry
  // and the panel rerenders with `children` / `parent` populated.
  const handleSelectRelatedItem = useCallback((next) => {
    if (!next?.slug) return;
    trackEvent('explore', 'explore_navigate_variation_chain', next.slug);
    if (relatedNavMode === 'page') {
      router.replace(`/explore/item/${encodeURIComponent(next.slug)}`);
    } else {
      navigateLightboxTo(next);
    }
    fetchExploreItem(next.slug);
  }, [relatedNavMode, router, navigateLightboxTo, fetchExploreItem]);

  useEffect(() => {
    if (!modelSlug) { setModel(null); return; }
    const found = useModelStore.getState().getModelBySlug(modelSlug);
    if (found) { setModel(found); return; }
    if (fetchedSlugsRef.current[modelSlug]) return;
    fetchedSlugsRef.current[modelSlug] = true;
    useModelStore.getState().fetchModelBySlug(modelSlug).then(m => { if (m) setModel(m); });
  }, [modelSlug]);

  const editableParams = useMemo(() => {
    if (!model?.parameters || !Array.isArray(model.parameters)) return [];
    return model.parameters.filter(p => {
      if (!p?.name || !p?.ui?.component) return false;
      const c = p.ui.component;
      return c === 'select' || c === 'toggle' || c === 'slider' || c === 'number';
    });
  }, [model]);

  const handleParamChange = useCallback((name, val) => setLocalParams(prev => ({ ...prev, [name]: val })), [setLocalParams]);

  const handleModelSelect = useCallback(() => {
    openModal('ModelSelectorDetailedModal', {
      modelIds: model?.id ? [model.id] : [],
      maxModels: 1,
      outputs: [activeMediaType],
      // Cross-media remix from an image source needs an input filter so the
      // user only sees models that actually accept an image (e.g. i2v models
      // when the OUTPUT TYPE is video). Without this they could pick a
      // text-to-video model that has nowhere to plug the source image into.
      inputs: modelSelectorInputs,
      shouldCloseOnSelect: true,
      onChange: (ids) => {
        if (ids?.length) {
          const sel = useModelStore.getState().getModelById(ids[0]);
          if (sel) { setModelSlug(sel.slug); setLocalParams({}); }
        }
        useModalStore.getState().actions.closeModal();
      },
    });
  }, [model, activeMediaType, modelSelectorInputs, openModal, setModelSlug, setLocalParams]);

  const hasLyrics = resolvedItem.musicContext?.lyrics;
  const inputStyle = [panelStyles.textArea, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary, color: colors.text_color_primary }];

  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  const handleCopyWithFeedback = useCallback(async () => {
    if (copied) return;
    await handleCopyPrompt();
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2200);
  }, [copied, handleCopyPrompt]);
  useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); }, []);

  const [downloading, setDownloading] = useState(false);
  const spinAnim = useRef(new Animated.Value(0)).current;
  const spinLoopRef = useRef(null);

  // Prompt expand/collapse — show first 6 lines by default with a Show more toggle.
  // We use a two-pronged detection so the toggle reliably appears even when
  // hidden-Text onLayout doesn't fire on react-native-web:
  //   1. Heuristic: char count / explicit newlines (instant, cheap, slightly fuzzy).
  //   2. Layout measurement: hidden full-text height vs. the 6-line cap (precise).
  // The measurement refines/overrides the heuristic when it lands.
  const PROMPT_LINE_HEIGHT = 22;
  const PROMPT_MAX_LINES = 6;
  const PROMPT_MAX_HEIGHT = PROMPT_LINE_HEIGHT * PROMPT_MAX_LINES;
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [promptFullHeight, setPromptFullHeight] = useState(0);

  // Section open/close state — most top-level fields default to open. Negative
  // prompt defaults closed since it's typically noisy boilerplate. Lyrics is
  // owned by the parent so it persists with the item; defaults closed there.
  const [promptOpen, setPromptOpen] = useState(true);
  const [negPromptOpen, setNegPromptOpen] = useState(false);
  const [musicDescOpen, setMusicDescOpen] = useState(true);

  useEffect(() => {
    setPromptExpanded(false);
    setPromptFullHeight(0);
    setPromptOpen(true);
    setNegPromptOpen(false);
    setMusicDescOpen(true);
  }, [resolvedItem?.id]);

  const promptText = resolvedItem?.prompt || '';
  const promptHeuristicClamped = useMemo(() => {
    if (!promptText) return false;
    const newlineCount = (promptText.match(/\n/g) || []).length;
    if (newlineCount >= PROMPT_MAX_LINES) return true;
    // The right panel is ~340px wide and renders text at ~14px → roughly
    // 50–55 visible characters per line. 6 lines * 50 ≈ 300 chars is a safe
    // lower bound that virtually never produces a false positive on the
    // 6-line cap, and triggers reliably for prompts that wrap past it.
    return promptText.length > 300;
  }, [promptText]);

  const promptIsClamped = promptFullHeight > 0
    ? promptFullHeight > PROMPT_MAX_HEIGHT + 2
    : promptHeuristicClamped;

  useEffect(() => {
    if (downloading) {
      spinAnim.setValue(0);
      spinLoopRef.current = Animated.loop(
        Animated.timing(spinAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
      );
      spinLoopRef.current.start();
    } else {
      spinLoopRef.current?.stop();
      spinAnim.setValue(0);
    }
  }, [downloading]);

  const spinStyle = { transform: [{ rotate: spinAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }] };

  const handleDownloadPress = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const labelPart = resolvedItem.displayName || resolvedItem.modelSlug || 'gab';
      const filename = `${labelPart.replace(/\s+/g, '-').toLowerCase()}-${resolvedItem.slug || resolvedItem.id}`;
      await downloadMedia(resolvedItem.cdnPath, filename);
      onDownload?.();
    } catch (_) {}
    setDownloading(false);
  }, [downloading, resolvedItem, onDownload]);

  return (
    <>
      {/* ── Anonymous-visitor sign-up CTA ──
          Renders only when there's no auth token at all. Sits at the very
          top of the scrollable panel content so it's the first thing an
          anonymous viewer sees after the media + header. */}
      {isAnon && <LightboxSignupCTA mediaType={resolvedItem.mediaType} />}

      {/* ── Prompt ── */}
      <View style={{ gap: 6 }}>
        <View style={panelStyles.fieldHeaderRow}>
          <Pressable onPress={() => setPromptOpen(p => !p)} style={panelStyles.collapseLabel} hitSlop={6}>
            <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>PROMPT</Text>
            <Icon name={promptOpen ? 'chevron-up' : 'chevron-down'} size={9} color={colors.text_color_tertiary} />
          </Pressable>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Pressable onPress={handleDownloadPress} style={[panelStyles.copyBtn, { backgroundColor: colors.solid_color_tertiary }]} hitSlop={6}>
              <Animated.View style={downloading ? spinStyle : undefined}>
                <Icon name={downloading ? 'circle-notch' : 'download'} size={11} color={colors.text_color_secondary} />
              </Animated.View>
            </Pressable>
            <Pressable onPress={handleCopyWithFeedback} style={[panelStyles.copyBtn, { backgroundColor: colors.solid_color_tertiary }]} hitSlop={6}>
              {copied ? (
                <Text size="xxs" weight="semiBold" color="secondary">Copied!</Text>
              ) : (
                <Icon name="copy" size={11} color={colors.text_color_secondary} />
              )}
            </Pressable>
          </View>
        </View>
        {promptOpen && (
          <>
            <View>
              <Text
                size="s"
                color="primary"
                style={{ lineHeight: PROMPT_LINE_HEIGHT }}
                selectable
                numberOfLines={promptExpanded ? undefined : PROMPT_MAX_LINES}
              >
                {resolvedItem.prompt}
              </Text>
              {/* Off-screen measurement — uses onLayout to get the unclamped height
                  and infer whether the visible (clamped) text needs a Show more toggle. */}
              {promptFullHeight === 0 && (
                <Text
                  size="s"
                  style={[panelStyles.promptMeasure, { lineHeight: PROMPT_LINE_HEIGHT }]}
                  onLayout={(e) => {
                    const h = e?.nativeEvent?.layout?.height || 0;
                    if (h > 0) setPromptFullHeight(h);
                  }}
                >
                  {resolvedItem.prompt}
                </Text>
              )}
            </View>
            {promptIsClamped && (
              <Pressable onPress={() => setPromptExpanded(p => !p)} hitSlop={6}>
                <Text size="xs" weight="semiBold" color="secondary" style={{ marginTop: 2 }}>
                  {promptExpanded ? 'Show less' : 'Show more'}
                </Text>
              </Pressable>
            )}
          </>
        )}
      </View>

      {/* ── Negative prompt ── */}
      {resolvedItem.negativePrompt && (
        <View style={{ gap: 4 }}>
          <View style={panelStyles.fieldHeaderRow}>
            <Pressable onPress={() => setNegPromptOpen(p => !p)} style={panelStyles.collapseLabel} hitSlop={6}>
              <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>NEGATIVE PROMPT</Text>
              <Icon name={negPromptOpen ? 'chevron-up' : 'chevron-down'} size={9} color={colors.text_color_tertiary} />
            </Pressable>
            <CopyChip
              text={resolvedItem.negativePrompt}
              label="Negative prompt"
              analyticsKey="explore_copy_negative_prompt"
              itemId={resolvedItem.id}
              colors={colors}
            />
          </View>
          {negPromptOpen && (
            <ScrollView
              style={[panelStyles.negativePromptBox, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]}
              nestedScrollEnabled
              showsVerticalScrollIndicator={false}
            >
              <Text size="xs" color="secondary" selectable style={{ lineHeight: 18 }}>{resolvedItem.negativePrompt}</Text>
            </ScrollView>
          )}
        </View>
      )}

      {/* ── Parameters + category chips ── */}
      <ExploreDetailParameters
        parameters={resolvedItem.parameters}
        modelSlug={resolvedItem.modelSlug}
        mediaType={resolvedItem.mediaType}
        musicContext={resolvedItem.musicContext}
        sourceImage={resolvedItem.sourceImage}
        categories={resolvedItem.categories}
        onCategoryPress={onCategoryPress}
      />

      {/* ── Source image ── */}
      {/* Side-by-side Before/After only makes sense for image-to-image, where
          both endpoints are stills. For image-to-video / image-to-3d / etc.
          the "after" lives in the main media viewer (already on screen) and
          rendering a video URL inside an <Image> just shows blank. So for
          non-image outputs we just show the source image alone, full width.
          The frame uses the source's native aspect ratio (sourced from the
          parent item's metadata when available, otherwise measured on load)
          with a blurhash background so any letterboxing reads as the image's
          own dominant colors instead of as black/transparent. */}
      {resolvedItem.sourceImage?.cdnPath && (() => {
        const parentAspect = resolvedItem.parent?.metadata?.aspectRatio
          || (resolvedItem.parent?.metadata?.width && resolvedItem.parent?.metadata?.height
              ? resolvedItem.parent.metadata.width / resolvedItem.parent.metadata.height
              : null);
        const currentAspect = resolvedItem.metadata?.aspectRatio
          || (resolvedItem.metadata?.width && resolvedItem.metadata?.height
              ? resolvedItem.metadata.width / resolvedItem.metadata.height
              : null);
        // Shared aspect ratio for the side-by-side comparison: picking the
        // wider of the two ensures neither image gets cropped (the narrower
        // one just letterboxes against its blurhash). This guarantees both
        // tiles render at identical width AND height — flex:1 already gave
        // equal width, this gives equal height.
        const sharedAspect = Math.max(parentAspect || 1, currentAspect || 1);
        return (
          <View style={{ gap: 6 }}>
            <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>SOURCE IMAGE</Text>
            {resolvedItem.mediaType === 'image' ? (
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                <View style={{ flex: 1 }}>
                  <AspectImage
                    uri={resolvedItem.sourceImage.cdnPath}
                    blurhash={resolvedItem.parent?.blurhash}
                    aspectRatio={sharedAspect}
                    backgroundColor={colors.solid_color_secondary}
                    maxHeight={180}
                  />
                  <Text size="xxs" color="secondary" style={{ textAlign: 'center', marginTop: 4 }}>Before</Text>
                </View>
                <View style={{ flex: 1 }}>
                  <AspectImage
                    uri={resolvedItem.cdnPath}
                    blurhash={resolvedItem.blurhash}
                    aspectRatio={sharedAspect}
                    backgroundColor={colors.solid_color_secondary}
                    maxHeight={180}
                  />
                  <Text size="xxs" color="secondary" style={{ textAlign: 'center', marginTop: 4 }}>After</Text>
                </View>
              </View>
            ) : (
              <AspectImage
                uri={resolvedItem.sourceImage.cdnPath}
                blurhash={resolvedItem.parent?.blurhash}
                aspectRatio={parentAspect}
                backgroundColor={colors.solid_color_secondary}
                maxHeight={180}
              />
            )}
          </View>
        );
      })()}

      {/* ── Music context ── */}
      {resolvedItem.musicContext?.description && (
        <View style={{ gap: 4 }}>
          <View style={panelStyles.fieldHeaderRow}>
            <Pressable onPress={() => setMusicDescOpen(p => !p)} style={panelStyles.collapseLabel} hitSlop={6}>
              <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>DESCRIPTION</Text>
              <Icon name={musicDescOpen ? 'chevron-up' : 'chevron-down'} size={9} color={colors.text_color_tertiary} />
            </Pressable>
            <CopyChip
              text={resolvedItem.musicContext.description}
              label="Description"
              analyticsKey="explore_copy_music_description"
              itemId={resolvedItem.id}
              colors={colors}
            />
          </View>
          {musicDescOpen && (
            <View style={[panelStyles.musicContextCard, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]}>
              <Icon name="music" size={12} color={colors.text_color_secondary} />
              <Text size="xs" color="secondary" style={{ flex: 1, lineHeight: 18 }}>{resolvedItem.musicContext.description}</Text>
            </View>
          )}
        </View>
      )}
      {hasLyrics && (
        <View style={{ gap: 4 }}>
          <View style={panelStyles.fieldHeaderRow}>
            <Pressable onPress={() => setLyricsExpanded(p => !p)} style={panelStyles.collapseLabel} hitSlop={6}>
              <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>LYRICS</Text>
              <Icon name={lyricsExpanded ? 'chevron-up' : 'chevron-down'} size={9} color={colors.text_color_tertiary} />
            </Pressable>
            <CopyChip
              text={resolvedItem.musicContext.lyrics}
              label="Lyrics"
              analyticsKey="explore_copy_lyrics"
              itemId={resolvedItem.id}
              colors={colors}
            />
          </View>
          {lyricsExpanded && (
            <ScrollView style={[panelStyles.lyricsBox, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]} nestedScrollEnabled>
              <Text size="xs" color="secondary" style={{ lineHeight: 20 }} selectable>{resolvedItem.musicContext.lyrics}</Text>
            </ScrollView>
          )}
        </View>
      )}

      {/* ── Derivation chain ── */}
      {/* Children rendered first so the user sees "what else exists from this
          template" before they consider remixing it themselves — this is the
          single biggest unlock of the variation system on the consume side. */}
      {!hideVariationsSection && (
        <VariationsSection
          variations={resolvedItem.children}
          colors={colors}
          onSelect={handleSelectRelatedItem}
        />
      )}
      {/* Parent comes after — it's provenance/context; only relevant when
          we already know the user is looking at a derived item. The fallback
          mini-pill keeps a hint visible in the (rare) case where the detail
          fetch hasn't yet returned the parent for a child item. */}
      {resolvedItem.parent ? (
        <DerivedFromCard
          parent={resolvedItem.parent}
          derivationType={resolvedItem.derivationType}
          colors={colors}
          onPress={handleSelectRelatedItem}
        />
      ) : resolvedItem.parentItem ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
          <Icon name="code-branch" size={11} color={colors.text_color_tertiary} />
          <Text size="xxs" color="tertiary">{getDerivationMeta(resolvedItem.derivationType).label}</Text>
        </View>
      ) : null}

      {/* ── Customize divider ── */}
      <View style={panelStyles.customizeDivider}>
        <View style={[panelStyles.dividerLine, { backgroundColor: colors.border_color_secondary }]} />
        <Text size="xxs" weight="semiBold" color='tertiary' style={panelStyles.dividerLabel}>
          CUSTOMIZE
        </Text>
        <View style={[panelStyles.dividerLine, { backgroundColor: colors.border_color_secondary }]} />
      </View>

      {/* ── Output type ── */}
      {resolvedItem.mediaType === 'image' && (
        <View style={{ gap: 6 }}>
          <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>OUTPUT TYPE</Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {['image', 'video'].map(mt => {
              const active = activeMediaType === mt;
              return (
                <Pressable key={mt} onPress={() => setActiveMediaType(mt)} style={[panelStyles.typeBtn, { borderColor: active ? colors.text_color_primary : colors.border_color_secondary, backgroundColor: active ? colors.solid_color_secondary_dark : 'transparent' }]}>
                  <Icon name={mt === 'image' ? 'image' : 'video'} size={12} color={active ? colors.text_color_primary : colors.text_color_tertiary} />
                  <Text size="xs" weight={active ? 'semiBold' : 'regular'} color={active ? 'primary' : 'secondary'}>{mt.charAt(0).toUpperCase() + mt.slice(1)}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}

      {/* ── Model selector ── */}
      <View style={{ gap: 6 }}>
        <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>MODEL</Text>
        <Pressable style={[panelStyles.modelSelector, { backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary }]} onPress={handleModelSelect}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
            <Icon name="microchip" size={13} color={colors.text_color_secondary} />
            <Text size="s" color="primary" numberOfLines={1}>{model?.name || modelSlug || 'Select a model'}</Text>
          </View>
          <Icon name="chevron-right" size={11} color={colors.text_color_tertiary} />
        </Pressable>
      </View>

      {/* ── Parameters ── */}
      {editableParams.length > 0 && (
        <View style={{ gap: 8 }}>
          <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5, marginBottom: 5 }}>PARAMETERS</Text>
          <View style={{ gap: 10 }}>
            {editableParams.map(param => {
              const val = localParams[param.name] ?? param.value;
              const opts = param.validation?.options || [];
              const label = param.ui?.displayName || param.name;

              if (param.ui.component === 'toggle') {
                return (
                  <View key={param.name} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Text size="s" color="secondary">{label}</Text>
                    <Switch value={!!val} onValueChange={(v) => handleParamChange(param.name, v)} />
                  </View>
                );
              }

              // `number` / `slider` are free-form numeric inputs (e.g. seed,
              // face_limit). The select-style chip renderer below assumes
              // `validation.options` exists, which these don't have — so
              // without this branch the row rendered as just a label and an
              // empty space. Render a numeric TextInput with min/max/step
              // hints from `validation.number` so users can actually edit
              // these values from the lightbox customize panel.
              if (param.ui.component === 'number' || param.ui.component === 'slider') {
                const numCfg = param.validation?.number || {};
                const placeholderBits = [];
                if (numCfg.min !== undefined) placeholderBits.push(`min ${numCfg.min}`);
                if (numCfg.max !== undefined) placeholderBits.push(`max ${numCfg.max}`);
                const placeholder = placeholderBits.length > 0
                  ? placeholderBits.join(' · ')
                  : (param.description || 'Optional');
                const stringVal = (val === null || val === undefined) ? '' : String(val);
                return (
                  <View key={param.name} style={{ gap: 6 }}>
                    <Text size="xxs" weight="medium" color="tertiary">{label}</Text>
                    <TextInput
                      style={[inputStyle, panelStyles.numericInput]}
                      value={stringVal}
                      onChangeText={(text) => {
                        const cleaned = text.replace(/[^0-9.\-]/g, '');
                        if (cleaned === '' || cleaned === '-') {
                          handleParamChange(param.name, null);
                          return;
                        }
                        const n = Number(cleaned);
                        handleParamChange(param.name, Number.isNaN(n) ? null : n);
                      }}
                      keyboardType="numeric"
                      inputMode="numeric"
                      placeholder={placeholder}
                      placeholderTextColor={colors.text_color_tertiary}
                    />
                  </View>
                );
              }

              if (opts.length === 0) return null;
              return (
                <View key={param.name} style={{ gap: 6 }}>
                  <Text size="xxs" weight="medium" color="tertiary">{label}</Text>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 5 }}>
                    {opts.map(o => {
                      const active = val === o.value || val === o.id;
                      return (
                        <Pressable
                          key={o.value || o.id}
                          onPress={() => handleParamChange(param.name, o.value || o.id)}
                          style={[panelStyles.paramChip, {
                            borderColor: active ? colors.text_color_primary : colors.border_color_secondary,
                            backgroundColor: active ? colors.solid_color_secondary_dark : colors.solid_color_secondary,
                          }]}
                        >
                          <Text size="xs" weight={active ? 'semiBold' : 'regular'} color={active ? 'primary' : 'secondary'}>
                            {o.label || o.name || String(o.value || o.id)}
                          </Text>
                        </Pressable>
                      );
                    })}
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}

      {/* ── Music customization ── */}
      {activeMediaType === 'music' && (
        <>
          <View style={{ gap: 6 }}>
            <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>MUSIC STYLE</Text>
            <TextInput style={[inputStyle, { minHeight: 110 }]} placeholder="Genre, mood, tempo..." placeholderTextColor={colors.text_color_tertiary} value={musicDescription} onChangeText={setMusicDescription} multiline textAlignVertical="top" />
          </View>
          <View style={{ gap: 6 }}>
            <Text size="xxs" weight="semiBold" color="tertiary" style={{ letterSpacing: 0.5 }}>LYRICS</Text>
            <TextInput style={[inputStyle, { minHeight: 180 }]} placeholder="Write or paste lyrics..." placeholderTextColor={colors.text_color_tertiary} value={musicLyrics} onChangeText={setMusicLyrics} multiline textAlignVertical="top" />
          </View>
        </>
      )}
    </>
  );
});

const panelStyles = StyleSheet.create({
  // Sticky header: title + meta row, sits above the panel ScrollView
  header: {
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 12,
    gap: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: 'transparent',
  },
  // On compact, the header doubles as the bottom-sheet handle: pull the top
  // padding tighter so the drag-handle pill sits closer to the top edge, and
  // signal interactivity on web with a pointer cursor.
  headerCompact: {
    paddingTop: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },
  // Title + meta on the left, expand chevron on the right (compact only).
  headerRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  // iOS-style drag-handle pill — purely visual, not actually draggable.
  // Communicates that the panel can be expanded.
  dragHandleContainer: { alignItems: 'center', marginBottom: 4 },
  dragHandle: { width: 36, height: 4, borderRadius: 2, opacity: 0.45 },
  // Tap target for the expand/collapse chevron in the compact header.
  expandToggle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb && { cursor: 'pointer', transition: 'background-color 0.15s' }),
  },
  // Meta row: model pill + stats
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  modelPill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    borderRadius: 6, paddingHorizontal: 8, paddingVertical: 4,
    ...(isWeb && { cursor: 'pointer' }),
  },
  plusPill: {
    flexDirection: 'row', alignItems: 'center', gap: 3,
    backgroundColor: '#7c3aed', borderRadius: 6,
    paddingHorizontal: 7, paddingVertical: 4,
  },
  statChip: { flexDirection: 'row', alignItems: 'center', gap: 3 },

  // Copy button
  copyBtn: {
    minWidth: 28, height: 28, borderRadius: 7,
    alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 8,
    ...(isWeb && { cursor: 'pointer' }),
  },

  // Shared header row for fields that pair a label with an inline copy chip
  // (negative prompt, music description, lyrics). Keeps the chip flush right
  // and vertically centered with the label.
  fieldHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // Pressable wrapper for collapsible section labels — pairs the label with a
  // chevron and grows to fill the header row so the entire left side is a hit
  // target without overlapping the right-side action chips.
  collapseLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 1,
    ...(isWeb && { cursor: 'pointer' }),
  },

  // Off-screen prompt measurement — used to detect clamping for the "Show more" toggle
  promptMeasure: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    opacity: 0,
    pointerEvents: 'none',
    zIndex: -1,
  },

  // Negative prompt
  negativePromptBox: { borderRadius: 8, borderWidth: 1, padding: 10, maxHeight: 100 },

  // Music context
  musicContextCard: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 8,
    borderRadius: 10, borderWidth: 1, padding: 10,
  },
  lyricsBox: { borderRadius: 8, borderWidth: 1, padding: 12, maxHeight: 180 },

  // Categories
  categoryPill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 1,
    ...(isWeb && { cursor: 'pointer' }),
  },

  // Customize divider
  customizeDivider: { flexDirection: 'row', alignItems: 'center', gap: 8, marginVertical: 2 },
  dividerLine: { flex: 1, height: 1 },
  dividerLabel: { fontSize: 10, letterSpacing: 1, paddingHorizontal: 4 },

  // Output type + model selector
  typeBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8, borderWidth: 1, ...(isWeb && { cursor: 'pointer' }) },
  modelSelector: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: 10, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10, ...(isWeb && { cursor: 'pointer' }) },

  // Parameter chips
  paramChip: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 7, borderWidth: 1, ...(isWeb && { cursor: 'pointer' }) },

  // Old compat
  inlineAction: { width: 28, height: 28, borderRadius: 6, justifyContent: 'center', alignItems: 'center', ...(isWeb && { cursor: 'pointer' }) },
  badge: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 8, paddingVertical: 5, borderRadius: 6 },
  // multiline TextInputs in the Customize section. On web, expose the native
  // textarea resize handle so users can drag to grow/shrink. On native we leave
  // the multiline behavior alone — RN textareas already grow with content.
  textArea: { borderRadius: 8, borderWidth: 1, padding: 10, fontSize: 13, textAlignVertical: 'top', lineHeight: 18, ...(isWeb && { outlineStyle: 'none', resize: 'vertical' }) },
  // Tighter, single-line variant of `textArea` used for numeric parameter
  // inputs (seed, face_limit). Overrides the multi-line text area defaults
  // so the field reads as a compact form input rather than a textarea.
  numericInput: { minHeight: 36, paddingVertical: 8, textAlignVertical: 'center', lineHeight: 16, ...(isWeb && { resize: 'none' }) },
  mediaBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, borderWidth: 1 },
});

// ── Main Lightbox ───────────────────────────────────────────────────────────

const ExploreLightbox = React.memo(() => {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const { t } = useTranslation();
  const router = useRouter();
  const { width: winW, height: winH } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  // On iOS/Android the lightbox renders full-screen behind the status bar /
  // notch / Dynamic Island. Push the top action buttons below the safe-area
  // inset so they don't end up under the clock/battery and become untappable.
  const topButtonOffset = Math.max(16, (insets.top || 0) + 8);
  const containerRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const mediaFadeAnim = useRef(new Animated.Value(1)).current;

  const isOpen = useExploreLightboxStore(s => s.isOpen);
  const item = useExploreLightboxStore(s => s.item);
  const snapshotItems = useExploreLightboxStore(s => s.items);
  const { closeExploreLightbox, navigateTo, consumeInitialMediaType } = useExploreLightboxStore(s => s.actions);

  // Prefer the live explore feed for navigation so the user can keep scrolling
  // through items beyond the initial snapshot. Falls back to the snapshot if
  // the lightbox was opened from a non-feed context (e.g. a deep link).
  const liveItems = useExploreStore(s => s.items);
  const explorePagination = useExploreStore(s => s.pagination);
  const isExploreLoading = useExploreStore(s => s.isLoading);
  const fetchFeed = useExploreStore(s => s.actions.fetchFeed);
  const items = useMemo(() => {
    if (Array.isArray(liveItems) && liveItems.length > 0) return liveItems;
    return snapshotItems || [];
  }, [liveItems, snapshotItems]);

  const fetchItem = useExploreStore(s => s.actions.fetchItem);
  const trackExploreEvent = useExploreStore(s => s.actions.trackEvent);
  const cloneItem = useExploreStore(s => s.actions.cloneItem);
  const storedItem = useExploreStore(s => item?.id ? s.itemsById[item.id] : null);
  const resolvedItem = storedItem || item;
  const addToast = useToastStore(s => s.actions.addToast);
  const openModal = useModalStore(s => s.actions.openModal);
  const closeModal = useModalStore(s => s.actions.closeModal);
  const isPlus = useAuthStore(s => s.user?.flags?.isPlus);
  const favoriteExploreItemIds = useAuthStore(s => s.user?.favoriteExploreItemIds);
  const toggleFavoriteExploreItem = useAuthStore(s => s.toggleFavoriteExploreItem);
  const isFavorited = !!(resolvedItem?.id && favoriteExploreItemIds?.includes(resolvedItem.id));
  const heartScaleAnim = useRef(new Animated.Value(1)).current;

  const [isCloning, setIsCloning] = useState(false);
  const [lyricsExpanded, setLyricsExpanded] = useState(false);

  const [remixInput, setRemixInput] = useState('');
  const [activeMediaType, setActiveMediaType] = useState('image');
  const [modelSlug, setModelSlug] = useState(null);
  const [localParams, setLocalParams] = useState({});
  const [musicDescription, setMusicDescription] = useState('');
  const [musicLyrics, setMusicLyrics] = useState('');

  const currentIndex = useMemo(() => items?.findIndex(i => i.id === resolvedItem?.id) ?? -1, [items, resolvedItem?.id]);
  const canPaginate = !!explorePagination?.hasMore && items === liveItems;
  const hasPrev = currentIndex > 0;
  const hasNext = (currentIndex >= 0 && currentIndex < (items?.length || 0) - 1) || canPaginate;

  // Prefetch the next page when the user is navigating near the end of the
  // current feed slice, so the lightbox can keep flowing past page 1.
  useEffect(() => {
    if (!isOpen) return;
    if (!canPaginate || isExploreLoading) return;
    if (currentIndex < 0) return;
    const distanceFromEnd = (items?.length || 0) - 1 - currentIndex;
    if (distanceFromEnd <= 5) {
      fetchFeed(explorePagination.page + 1);
    }
  }, [isOpen, canPaginate, isExploreLoading, currentIndex, items?.length, explorePagination?.page, fetchFeed]);

  const [panelScrolled, setPanelScrolled] = useState(false);
  const handlePanelScroll = useCallback((e) => {
    const y = e?.nativeEvent?.contentOffset?.y || 0;
    const next = y > 4;
    setPanelScrolled(prev => (prev !== next ? next : prev));
  }, []);

  // ── Compact (≤ SM) bottom-sheet behavior ──────────────────────────────────
  // Below SM_BREAKPOINT the right panel becomes a draggable-feeling bottom
  // sheet that defaults to a "peek" state (just the title + model pill
  // visible) and expands to show the full sidebar contents on chevron tap.
  const isCompact = isXS || isSM;
  const PEEK_HEIGHT = 92; // matches the drag-handle + 1-line title + meta row
  const compactExpandedTop = Math.max(80, Math.round(winH * 0.18));
  const compactPeekTop = Math.max(
    compactExpandedTop + 60,
    winH - PEEK_HEIGHT,
  );

  const [compactPanelExpanded, setCompactPanelExpanded] = useState(false);
  const compactPanelAnim = useRef(new Animated.Value(0)).current;
  const compactPanelTop = compactPanelAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [compactPeekTop, compactExpandedTop],
  });

  const toggleCompactPanel = useCallback(() => {
    const opening = !compactPanelExpanded;
    setCompactPanelExpanded(opening);
    mediumTap();
    Animated.spring(compactPanelAnim, {
      toValue: opening ? 1 : 0,
      useNativeDriver: false, // animating `top` — layout prop, can't native-drive
      speed: 14,
      bounciness: 6,
    }).start();
  }, [compactPanelExpanded, compactPanelAnim]);

  // ── Compact swipe-to-navigate (TikTok-style) ──────────────────────────────
  // On compact, a vertical pan on the media area navigates between explore
  // items. The current media translates with the finger (with rubber-band
  // resistance at the edges of the feed). On commit (distance OR velocity
  // threshold) we slide the current item off, swap, and spring the next item
  // in from the opposite side. We bypass the existing fade animation during a
  // swipe so the transition feels purely gesture-driven.
  const SWIPE_DISTANCE_THRESHOLD = 80;
  const SWIPE_VELOCITY_THRESHOLD = 0.45;
  const SWIPE_RESISTANCE = 0.28;
  const swipeY = useRef(new Animated.Value(0)).current;
  const isSwipingRef = useRef(false);

  // Refs so the PanResponder closure (built once) always sees fresh nav state.
  const swipeNavStateRef = useRef({});
  swipeNavStateRef.current = {
    hasPrev,
    hasNext,
    items,
    currentIndex,
    canPaginate,
    isExploreLoading,
    page: explorePagination?.page,
    fetchFeed,
    navigateTo,
    expanded: compactPanelExpanded,
    isCompact,
    winH,
  };

  const settleSwipe = useCallback((toValue, onDone) => {
    Animated.spring(swipeY, {
      toValue,
      useNativeDriver: true,
      speed: 16,
      bounciness: 5,
    }).start(({ finished }) => { if (finished && onDone) onDone(); });
  }, [swipeY]);

  const commitSwipe = useCallback((direction) => {
    const s = swipeNavStateRef.current;
    const offTarget = direction === 1 ? -s.winH : s.winH;
    isSwipingRef.current = true;
    mediumTap();
    Animated.timing(swipeY, {
      toValue: offTarget,
      duration: 180,
      useNativeDriver: true,
    }).start(async () => {
      // Pre-position the incoming item on the opposite side so it slides in
      // rather than popping. We bypass `handleNavigate` here to skip the fade.
      swipeY.setValue(-offTarget);

      let didNavigate = false;
      if (direction === 1) {
        const nextItem = s.items?.[s.currentIndex + 1];
        if (nextItem) { s.navigateTo(nextItem); didNavigate = true; }
        else if (s.canPaginate && !s.isExploreLoading) {
          try {
            const fetched = await s.fetchFeed((s.page || 0) + 1);
            if (Array.isArray(fetched) && fetched.length > 0) {
              s.navigateTo(fetched[0]);
              didNavigate = true;
            }
          } catch (_) {}
        }
      } else {
        const prevItem = s.items?.[s.currentIndex - 1];
        if (prevItem) { s.navigateTo(prevItem); didNavigate = true; }
      }

      // If we couldn't navigate (e.g. pagination came back empty), bounce the
      // outgoing item back from the off-screen position to center.
      if (!didNavigate) swipeY.setValue(offTarget);

      Animated.spring(swipeY, {
        toValue: 0,
        useNativeDriver: true,
        speed: 14,
        bounciness: 4,
      }).start(() => { isSwipingRef.current = false; });
    });
  }, [swipeY]);

  const swipePanResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: (_, g) => {
      const s = swipeNavStateRef.current;
      if (!s.isCompact || s.expanded) return false;
      if (isSwipingRef.current) return false;
      // Claim only clearly-vertical swipes so taps and any horizontal/inner
      // gestures (video play tap, music seek-bar drag) still pass through.
      return Math.abs(g.dy) > 8 && Math.abs(g.dy) > Math.abs(g.dx) * 1.4;
    },
    onPanResponderTerminationRequest: () => false,
    onPanResponderGrant: () => {
      swipeY.stopAnimation();
      swipeY.setValue(0);
    },
    onPanResponderMove: (_, g) => {
      const s = swipeNavStateRef.current;
      let dy = g.dy;
      // Rubber-band when swiping past the edges of the feed.
      if (dy > 0 && !s.hasPrev) dy *= SWIPE_RESISTANCE;
      if (dy < 0 && !s.hasNext) dy *= SWIPE_RESISTANCE;
      swipeY.setValue(dy);
    },
    onPanResponderRelease: (_, g) => {
      const s = swipeNavStateRef.current;
      const fastUp = g.vy < -SWIPE_VELOCITY_THRESHOLD;
      const fastDown = g.vy > SWIPE_VELOCITY_THRESHOLD;
      const farUp = g.dy < -SWIPE_DISTANCE_THRESHOLD;
      const farDown = g.dy > SWIPE_DISTANCE_THRESHOLD;
      if ((farUp || fastUp) && s.hasNext) commitSwipe(1);
      else if ((farDown || fastDown) && s.hasPrev) commitSwipe(-1);
      else settleSwipe(0);
    },
    onPanResponderTerminate: () => settleSwipe(0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Reset swipe offset whenever the lightbox closes or the user crosses the
  // breakpoint so the next mount of the media starts at translateY: 0.
  useEffect(() => {
    if (!isOpen || !isCompact) { swipeY.setValue(0); isSwipingRef.current = false; }
  }, [isOpen, isCompact, swipeY]);

  // Subtle opacity dip + scale during drag for that TikTok feel. Memoized so
  // the Animated graph stays stable across renders (otherwise React would
  // detach/reattach the interpolation + multiply node every commit).
  const swipeMediaOpacity = useMemo(() => swipeY.interpolate({
    inputRange: [-winH, -winH / 3, 0, winH / 3, winH],
    outputRange: [0.35, 0.85, 1, 0.85, 0.35],
    extrapolate: 'clamp',
  }), [swipeY, winH]);
  const swipeMediaScale = useMemo(() => swipeY.interpolate({
    inputRange: [-winH, 0, winH],
    outputRange: [0.92, 1, 0.92],
    extrapolate: 'clamp',
  }), [swipeY, winH]);
  const compactMediaOpacity = useMemo(
    () => Animated.multiply(mediaFadeAnim, swipeMediaOpacity),
    [mediaFadeAnim, swipeMediaOpacity],
  );

  // ── Top-right download button (compact only) ──────────────────────────────
  // Mirrors the in-panel download chip but lives in the top-right action
  // cluster on small screens so it's reachable without expanding the sheet.
  const [topDownloading, setTopDownloading] = useState(false);
  const topDownloadSpinAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!topDownloading) { topDownloadSpinAnim.setValue(0); return; }
    topDownloadSpinAnim.setValue(0);
    const loop = Animated.loop(
      Animated.timing(topDownloadSpinAnim, { toValue: 1, duration: 700, useNativeDriver: true }),
    );
    loop.start();
    return () => loop.stop();
  }, [topDownloading]);
  const topDownloadSpinStyle = {
    transform: [{ rotate: topDownloadSpinAnim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) }],
  };

  const handleTopDownload = useCallback(async () => {
    if (!resolvedItem?.slug || topDownloading) return;
    trackEvent('explore', 'explore_download', resolvedItem.slug);
    trackExploreEvent(resolvedItem.slug, 'download');
    setTopDownloading(true);
    try {
      const labelPart = resolvedItem.displayName || resolvedItem.modelSlug || 'gab';
      const filename = `${labelPart.replace(/\s+/g, '-').toLowerCase()}-${resolvedItem.slug}`;
      await downloadMedia(resolvedItem.cdnPath, filename);
      mediumTap();
    } catch (_) {}
    setTopDownloading(false);
  }, [resolvedItem, topDownloading, trackExploreEvent]);

  useEffect(() => {
    if (!resolvedItem) return;
    setRemixInput('');
    // Prefer a one-shot initialMediaType (e.g. card "Make Video") so the
    // user lands on video output with the prompt visible — not auto-charged.
    const preferredMediaType = consumeInitialMediaType();
    const nextMediaType = preferredMediaType || resolvedItem.mediaType || 'image';
    setActiveMediaType(nextMediaType);
    setModelSlug(resolvedItem.modelSlug || null);
    // Image→video must not inherit the source image's parameter bag (often
    // includes invalid durations like "5"/"10" that Veo rejects).
    const isImageToVideo = nextMediaType === 'video' && resolvedItem.mediaType === 'image';
    setLocalParams(isImageToVideo ? {} : (resolvedItem.parameters || {}));
    setMusicDescription(resolvedItem.musicContext?.description || '');
    setMusicLyrics(resolvedItem.musicContext?.lyrics || '');
    setLyricsExpanded(false);
    setPanelScrolled(false);
  }, [resolvedItem?.id, consumeInitialMediaType]);

  // When the user toggles the OUTPUT TYPE tabs in the customize panel for an
  // image source, swap the model to match:
  //   - "video" tab: pick the first available image-to-video model (so the
  //     model selector + generation pipeline don't try to use an image model).
  //   - "image" tab: restore whatever model originally produced the source
  //     image, so toggling back is non-destructive.
  // Music/3D tabs intentionally aren't auto-swapped here — the user picks
  // those from the model selector explicitly.
  useEffect(() => {
    if (!resolvedItem || resolvedItem.mediaType !== 'image') return;
    if (activeMediaType === 'image') {
      // Restore the original image model whenever we land back on the image
      // tab. Safe to call even when modelSlug already matches — setState
      // bails on identical values.
      setModelSlug(resolvedItem.modelSlug || null);
      setLocalParams(resolvedItem.parameters || {});
      return;
    }
    if (activeMediaType !== 'video') return;
    // Drop image params immediately — otherwise a fast Generate can forward
    // stale explore values (e.g. duration "5"/"10") onto Veo and fail
    // constraint validation. Model defaults apply until the user picks chips.
    setLocalParams({});
    let cancelled = false;
    (async () => {
      const store = useModelStore.getState();
      // Try cache first for an instant swap; fall back to the resolver which
      // fetches the preferred i2v slugs in priority order if none are cached
      // yet (e.g. first lightbox open in a session).
      let pickSlug = null;
      for (const slug of I2V_SLUG_PREFERENCES) {
        if (store.getModelBySlug?.(slug)) { pickSlug = slug; break; }
      }
      if (!pickSlug) {
        const id = await resolveImageToVideoModelId();
        if (cancelled) return;
        const m = id ? useModelStore.getState().getModelById(id) : null;
        pickSlug = m?.slug || null;
      }
      if (cancelled) return;
      if (pickSlug) {
        setModelSlug(pickSlug);
      }
    })();
    return () => { cancelled = true; };
  }, [activeMediaType, resolvedItem?.id, resolvedItem?.mediaType, resolvedItem?.modelSlug, resolvedItem?.parameters]);

  // Filter the model picker to models that can actually consume the source.
  // Image source + video output → require image input (i.e. i2v models).
  // We don't enforce inputs for same-media tabs because the user might want
  // a pure text-to-image model even when remixing from an image.
  const modelSelectorInputs = useMemo(() => {
    if (resolvedItem?.mediaType === 'image' && activeMediaType === 'video') return ['image'];
    return undefined;
  }, [resolvedItem?.mediaType, activeMediaType]);

  // Reset compact panel to "peek" whenever the lightbox opens, the active item
  // changes, or the viewport crosses the SM breakpoint. Without this, navigating
  // to a new item while expanded would keep the sheet covering the new media.
  useEffect(() => {
    if (!isCompact) return;
    setCompactPanelExpanded(false);
    compactPanelAnim.setValue(0);
  }, [isOpen, resolvedItem?.id, isCompact, compactPanelAnim]);

  useEffect(() => {
    if (!isOpen) return;
    fadeAnim.setValue(0);
    Animated.timing(fadeAnim, { toValue: 1, duration: 200, useNativeDriver: isWeb ? false : true }).start();
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && resolvedItem?.slug) trackExploreEvent(resolvedItem.slug, 'view');
  }, [resolvedItem?.slug]);

  const prevUrlRef = useRef(null);

  useEffect(() => {
    if (!isWeb) return;
    if (isOpen && resolvedItem?.slug) {
      // We only sync the URL to /explore/item/<slug> when the lightbox is
      // hosted on an explore route in the first place. The lightbox is
      // also mounted on the public landing page (`/`) so unauth visitors
      // can preview items, but in that context we *don't* want to change
      // the URL — otherwise closing the lightbox would yank the visitor
      // off the landing page (they'd end up at /explore with no way back
      // to the marketing copy they were reading).
      const path = window.location.pathname;
      const isOnExploreRoute = path === '/explore' || path.startsWith('/explore/');
      if (!isOnExploreRoute) return;

      // Capture the URL to restore when the lightbox closes. We only set
      // this once per open session (subsequent item-to-item navigations
      // inside the lightbox must NOT overwrite it, otherwise close-restore
      // would land on a stale item path).
      //
      // Two scenarios:
      //   1. User opened the lightbox from /explore (or filters) → capture
      //      that exact URL so close restores filters/scroll perfectly.
      //   2. User landed directly on /explore/item/<slug> (deep link, share,
      //      browser refresh) → there's no "previous" URL, so default to
      //      /explore (preserving any query string for filters). Without
      //      this fallback, closing did nothing and the URL stayed stuck
      //      on the item path.
      if (!prevUrlRef.current) {
        const isOnItemPath = /^\/explore\/item\//.test(path);
        const search = window.location.search || '';
        prevUrlRef.current = isOnItemPath
          ? `/explore${search}`
          : path + window.location.search;
      }
      // Preserve any existing query string (filters, search, etc.) on the path.
      const search = window.location.search || '';
      try { window.history.replaceState(null, '', `/explore/item/${resolvedItem.slug}${search}`); } catch (_) {}
    } else if (!isOpen && prevUrlRef.current) {
      try { window.history.replaceState(null, '', prevUrlRef.current); } catch (_) {}
      prevUrlRef.current = null;
    }
  }, [isOpen, resolvedItem?.slug]);

  useEffect(() => {
    if (!item?.slug) return;
    // Always fetch the detail endpoint when an item is opened in the lightbox.
    // Feed items are populated into `itemsById` by `fetchFeed`, but they only
    // include the basic serialized fields — they're missing detail-only ones
    // like `children` (variations) and `parent` (provenance). We use the
    // presence of `children` as the "is this a fully-detailed item?" marker
    // (it's always set, even to [], by the detail endpoint) so we refetch
    // exactly once per open without spamming the server on every navigate.
    if (storedItem && Array.isArray(storedItem.children)) return;
    fetchItem(item.slug);
  }, [item?.slug, storedItem?.children]);

  const handleNavigate = useCallback((nextItem) => {
    if (!nextItem) return;
    mediaFadeAnim.setValue(0);
    navigateTo(nextItem);
    Animated.timing(mediaFadeAnim, { toValue: 1, duration: 200, useNativeDriver: isWeb ? false : true }).start();
  }, [navigateTo, mediaFadeAnim]);

  // Navigate forward; if we're at the tail of the loaded slice but the feed
  // has more pages, fetch the next page first and then jump to its first item.
  const navigateForward = useCallback(async () => {
    if (currentIndex < 0) return;
    const nextItem = items[currentIndex + 1];
    if (nextItem) { handleNavigate(nextItem); return; }
    if (!canPaginate || isExploreLoading) return;
    const fetched = await fetchFeed(explorePagination.page + 1);
    if (Array.isArray(fetched) && fetched.length > 0) {
      handleNavigate(fetched[0]);
    }
  }, [currentIndex, items, canPaginate, isExploreLoading, fetchFeed, explorePagination?.page, handleNavigate]);

  useEffect(() => {
    if (!isWeb || !isOpen) return;
    const handler = (e) => {
      // If a modal is open on top of the lightbox, let the modal handle Escape
      // first — otherwise pressing Esc would close both the modal AND the
      // lightbox in a single keystroke.
      const modalOpen = useModalStore.getState().isOpen;
      if (e.key === 'Escape') {
        if (modalOpen) return;
        closeExploreLightbox();
        return;
      }
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;
      if (modalOpen) return;
      if (e.key === 'ArrowUp' && hasPrev) { e.preventDefault(); handleNavigate(items[currentIndex - 1]); }
      if (e.key === 'ArrowDown' && hasNext) { e.preventDefault(); navigateForward(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, hasPrev, hasNext, currentIndex, items, closeExploreLightbox, handleNavigate, navigateForward]);

  useEffect(() => {
    if (Platform.OS !== 'macos' || !isOpen) return undefined;
    const timer = setTimeout(() => {
      try { containerRef.current?.focus?.(); } catch (_) {}
    }, 100);
    return () => clearTimeout(timer);
  }, [isOpen, resolvedItem?.id]);

  const handleMacOSKeyDown = useCallback((event) => {
    if (!isOpen) return;
    const nativeEvent = event?.nativeEvent || {};
    const key = nativeEvent.key || nativeEvent.charactersIgnoringModifiers || nativeEvent.characters;
    const keyCode = nativeEvent.keyCode;
    const modalOpen = useModalStore.getState().isOpen;

    if (key === 'Escape' || keyCode === 53) {
      if (modalOpen) return;
      event?.preventDefault?.();
      closeExploreLightbox();
      return;
    }

    if (modalOpen) return;

    const isPrevKey = key === 'ArrowUp' || key === 'Up' || keyCode === 126;
    const isNextKey = key === 'ArrowDown' || key === 'Down' || keyCode === 125;

    if (isPrevKey && hasPrev) {
      event?.preventDefault?.();
      handleNavigate(items[currentIndex - 1]);
      return;
    }

    if (isNextKey && hasNext) {
      event?.preventDefault?.();
      navigateForward();
    }
  }, [isOpen, hasPrev, hasNext, currentIndex, items, closeExploreLightbox, handleNavigate, navigateForward]);

  useEffect(() => {
    if (Platform.OS !== 'macos' || !isOpen) return undefined;
    const MacMenuBridge = NativeModules.MacMenuBridge;
    if (!MacMenuBridge) return undefined;

    const emitter = new NativeEventEmitter(MacMenuBridge);
    const subscription = emitter.addListener('onKeyDown', (event) => {
      handleMacOSKeyDown({ nativeEvent: event, preventDefault: () => {} });
    });

    return () => subscription.remove();
  }, [isOpen, handleMacOSKeyDown]);

  const handleClose = useCallback(() => {
    Animated.timing(fadeAnim, { toValue: 0, duration: 150, useNativeDriver: isWeb ? false : true }).start(() => closeExploreLightbox());
  }, [fadeAnim, closeExploreLightbox]);

  // Route all gates through the shared `useRequireAuth` hook so anonymous
  // visitors get the unified `AuthCTAModal` (with contextual title/icon)
  // instead of the legacy `SignupModal`. Returns `true` when the action
  // should bail out (auth modal opened), matching the prior contract.
  const requireAuthShared = useRequireAuthHook();
  const requireAuth = useCallback((action) => {
    const ran = requireAuthShared(() => true, {
      source: `explore_lightbox_${action}`,
      title: action === 'favorite'
        ? 'Sign in to save this'
        : 'Sign in to create with this prompt',
      subtitle: action === 'favorite'
        ? 'Favorites stay tied to your account so they follow you across devices.'
        : 'Free accounts unlock remixing prompts and generating new images, video, music, and 3D.',
      icon: action === 'favorite' ? 'heart' : 'wand-magic-sparkles',
    });
    if (ran !== true) {
      trackEvent('explore', 'explore_auth_required', action);
      return true;
    }
    return false;
  }, [requireAuthShared]);

  const user = useAuthStore(s => s.user);
  const requirePlusFn = useCallback(() => {
    const model = modelSlug
      ? useModelStore.getState().getModelBySlug(modelSlug)
      : null;
    const isVideo = activeMediaType === 'video' || resolvedItem?.mediaType === 'video';
    if (model) {
      if (!isModelLockedForUser(user, model)) return false;
    } else {
      // Model doc not loaded yet — image stays free; video / other showcase gates.
      if (user?.flags?.isPlus || hasPaidCredits(user)) return false;
      if (!isVideo && resolvedItem?.mediaType === 'image') return false;
      if (!isVideo && !resolvedItem?.flags?.isPlusShowcase) return false;
    }
    trackEvent('explore', 'explore_upgrade_prompt', modelSlug || resolvedItem?.modelSlug);
    openModelAccessPaywall(openModal, {
      model,
      isVideo,
      title: t('explore.plusRequired'),
      subtitle: t('explore.plusRequiredSubtitle'),
    });
    return true;
  }, [user, openModal, t, modelSlug, activeMediaType, resolvedItem?.mediaType, resolvedItem?.modelSlug, resolvedItem?.flags?.isPlusShowcase]);

  const handleToggleFavorite = useCallback(async () => {
    if (!resolvedItem?.slug) return;
    if (requireAuth('favorite')) return;

    Animated.sequence([
      Animated.spring(heartScaleAnim, { toValue: 1.3, useNativeDriver: true, speed: 30, bounciness: 12 }),
      Animated.spring(heartScaleAnim, { toValue: 1, useNativeDriver: true, speed: 20, bounciness: 8 }),
    ]).start();

    try {
      const nowFavorited = await toggleFavoriteExploreItem(resolvedItem);
      mediumTap();
      trackEvent('explore', nowFavorited ? 'explore_favorite' : 'explore_unfavorite', resolvedItem.slug);
    } catch (_) {
      addToast('Failed to update favorite', 'error');
    }
  }, [resolvedItem, requireAuth, toggleFavoriteExploreItem, addToast, heartScaleAnim]);

  const buildFinalPrompt = useCallback((remix) => {
    const original = (resolvedItem?.prompt || '').trim();
    if (!remix) return original;
    return `${original}\n\n---\nRemix: ${remix}`;
  }, [resolvedItem?.prompt]);

  const runUsePrompt = useCallback(async () => {
    if (isCloning) return;
    setIsCloning(true);
    trackEvent('explore', 'explore_clone', resolvedItem?.slug);

    try {
      // The clone endpoint returns the resolved Model document, which is the
      // authoritative source for the ObjectId we need to send to the chat
      // pipeline. Falling back to the slug → id cache lookup only if the
      // clone call didn't run or didn't include a model.
      const clone = resolvedItem?.slug ? await cloneItem(resolvedItem.slug) : null;

      const store = useChatInputStore.getState();
      const pr = (resolvedItem?.prompt || '').trim();
      const ms = modelSlug;
      // Prefer the UI-selected model (already swapped to an i2v slug when the
      // user is on OUTPUT TYPE video). Clone's model is the SOURCE image model
      // and must not win here or create-video falls through to Veo 3.1 Fast.
      let mid = (ms ? useModelStore.getState().getModelBySlug(ms)?.id : null)
        || clone?.model?.id
        || null;
      let attachedFiles = [];

      // If the user is on an image and switched to "video", route through an
      // image-to-video model with the original image attached as the source.
      // The backend resolves the i2v model in the same call as the file mint.
      const isI2V = activeMediaType === 'video' && resolvedItem?.mediaType === 'image';
      if (isI2V) {
        const { file: sourceFile, i2vModelId } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
        const selected = ms ? useModelStore.getState().getModelBySlug(ms) : null;
        const selectedIsI2V = !!(selected?.slug && I2V_SLUG_PREFERENCES.includes(selected.slug));
        // Never keep a source-image model id here — that falls through to the
        // default text-to-video model (Veo 3.1 Fast) with bad param bags.
        mid = i2vModelId
          || (selectedIsI2V ? selected.id : null)
          || await resolveImageToVideoModelId();
        if (!mid) {
          addToast('No image-to-video model is available right now. Please try again later.', 'error');
          return;
        }
      }

      if (activeMediaType === 'video') store.initializeVideoGeneration(pr, mid, true, attachedFiles);
      else if (activeMediaType === 'music') store.initializeMusicGeneration(pr, mid, true, musicDescription || null);
      else if (activeMediaType === '3d') store.initialize3DGeneration(pr, mid, true, false);
      else store.initializeImageGeneration(pr, mid, true);

      const cs = useChatInputStore.getState();
      const ctxId = cs.currentContextId || 'new';
      const is = cs.inputStates?.[ctxId];
      if (is && mid) {
        // Snap explore/customize params onto the TARGET model only — never
        // forward raw image-item keys (or invalid durations) as globals.
        const mp = buildExploreSelectedParameters(mid, localParams, { ...(is.selectedParameters || {}) });
        useChatInputStore.setState({ inputStates: { ...cs.inputStates, [ctxId]: { ...is, selectedParameters: mp } } });
      }
      if (resolvedItem?.id && cs.pendingAutoSend) {
        useChatInputStore.setState({ pendingAutoSend: { ...useChatInputStore.getState().pendingAutoSend, exploreItemId: resolvedItem.id, exploreItemSlug: resolvedItem.slug } });
      }

      notifySuccess();
      addToast(t('explore.cloneSuccess'), 'success');
      closeExploreLightbox();
      router.push('/app');
    } catch (err) {
      if (err.status === 402 || err.data?.upgradeRequired) {
        const { isModelAccessError } = classifyResponseError({
          actions: err.data?.actions,
          errorMessage: err.message,
          errorCode: err.data?.errorCode,
        });
        if (isModelAccessError || /purchased credits/i.test(err.message || '')) {
          const model = modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null;
          openModelAccessPaywall(openModal, {
            model,
            isVideo: activeMediaType === 'video' || resolvedItem?.mediaType === 'video',
            title: t('explore.plusRequired'),
            subtitle: t('explore.plusRequiredSubtitle'),
          });
        } else {
          openModal('PricingModal', { title: t('explore.limitReached'), subtitle: t('explore.limitReachedSubtitle') });
        }
      } else addToast(err.message || 'Generation failed', 'error');
    } finally { setIsCloning(false); }
  }, [isCloning, resolvedItem, modelSlug, activeMediaType, musicDescription, localParams, cloneItem, addToast, t, router, openModal, closeExploreLightbox]);

  const handleUsePrompt = useCallback(() => {
    if (isCloning) return;
    if (requireAuth('generate')) return;
    // Gated models (video / Plus-only text, etc.): buy credits OR upgrade.
    if (requirePlusFn()) return;

    // Video is expensive — require an explicit confirm that surfaces credit cost.
    if (activeMediaType === 'video') {
      const copy = getExploreVideoConfirmCopy(t, modelSlug, localParams);
      openModal('ConfirmationModal', {
        title: copy.title,
        message: copy.message,
        confirmText: copy.confirmText,
        cancelText: copy.cancelText,
        onConfirm: () => {
          closeModal();
          runUsePrompt();
        },
      });
      return;
    }

    runUsePrompt();
  }, [isCloning, requireAuth, requirePlusFn, resolvedItem, activeMediaType, modelSlug, localParams, t, openModal, closeModal, runUsePrompt]);

  const runRemixSubmit = useCallback(async () => {
    if (!remixInput.trim() || isCloning) return;
    setIsCloning(true);
    trackEvent('explore', 'explore_remix_generate', resolvedItem?.slug);

    try {
      const store = useChatInputStore.getState();
      // Any time the source image is going to be attached as a conditioning
      // input (image-to-video, image-to-3D, image-to-image), the original
      // prompt is irrelevant — the image IS the context. Sending the original
      // text on top just pollutes the generation with stale instructions
      // authored for a different intent. Use ONLY the remix text in those cases.
      // Same-media remixes WITHOUT a source attachment (e.g. video→video,
      // music→music — currently no file attachment path) keep the original
      // prompt as the base so "Remix: X" can meaningfully refine it.
      const isCrossMediaRemix = !!resolvedItem?.mediaType && activeMediaType !== resolvedItem.mediaType;
      const isImageToImageRemix = activeMediaType === 'image' && resolvedItem?.mediaType === 'image';
      const usePromptOnly = isCrossMediaRemix || isImageToImageRemix;
      const remixText = remixInput.trim();
      const pr = usePromptOnly ? remixText : buildFinalPrompt(remixText);
      const ms = modelSlug;
      // Resolve slug → id from cache, falling back to a fetch so the chat
      // pipeline never receives a slug (which would 500 on ObjectId cast).
      let mid = null;
      if (ms) {
        mid = useModelStore.getState().getModelBySlug(ms)?.id || null;
        if (!mid) {
          try {
            const fetched = await useModelStore.getState().fetchModelBySlug(ms);
            mid = fetched?.id || null;
          } catch { /* fall through to null */ }
        }
      }
      let attachedFiles = [];

      // If the user is remixing an image and switched to "video", route through
      // an image-to-video model with the original image attached as the source.
      // The backend resolves the i2v model in the same call as the file mint.
      const isI2V = activeMediaType === 'video' && resolvedItem?.mediaType === 'image';
      if (isI2V) {
        const { file: sourceFile, i2vModelId } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
        const selected = ms ? useModelStore.getState().getModelBySlug(ms) : null;
        const selectedIsI2V = !!(selected?.slug && I2V_SLUG_PREFERENCES.includes(selected.slug));
        mid = i2vModelId
          || (selectedIsI2V ? selected.id : null)
          || await resolveImageToVideoModelId();
        if (!mid) {
          addToast('No image-to-video model is available right now. Please try again later.', 'error');
          return;
        }
      } else if (isImageToImageRemix) {
        // Image-to-image remix: attach the source image so the chosen model
        // (typically an i2i / edit model) uses it as the conditioning input
        // instead of generating from scratch. Reuse the same mint endpoint —
        // we only need the File record, not the i2v model resolution.
        const { file: sourceFile } = await mintExploreImageForVideo(resolvedItem.slug);
        if (sourceFile) attachedFiles = [sourceFile];
      }

      if (activeMediaType === 'video') store.initializeVideoGeneration(pr, mid, true, attachedFiles);
      else if (activeMediaType === 'music') store.initializeMusicGeneration(pr, mid, true, musicDescription || null);
      else if (activeMediaType === '3d') store.initialize3DGeneration(pr, mid, true, false);
      else store.initializeImageGeneration(pr, mid, true, attachedFiles);

      const cs = useChatInputStore.getState();
      const ctxId = cs.currentContextId || 'new';
      const is = cs.inputStates?.[ctxId];
      if (is && mid) {
        const mp = buildExploreSelectedParameters(mid, localParams, { ...(is.selectedParameters || {}) });
        useChatInputStore.setState({ inputStates: { ...cs.inputStates, [ctxId]: { ...is, selectedParameters: mp } } });
      }

      notifySuccess();
      addToast(t('explore.remixSuccess'), 'success');
      closeExploreLightbox();
      router.push('/app');
    } catch (err) {
      if (err.status === 402 || err.data?.upgradeRequired) {
        const { isModelAccessError } = classifyResponseError({
          actions: err.data?.actions,
          errorMessage: err.message,
          errorCode: err.data?.errorCode,
        });
        if (isModelAccessError || /purchased credits/i.test(err.message || '')) {
          const model = modelSlug ? useModelStore.getState().getModelBySlug(modelSlug) : null;
          openModelAccessPaywall(openModal, {
            model,
            isVideo: activeMediaType === 'video' || resolvedItem?.mediaType === 'video',
            title: t('explore.plusRequired'),
            subtitle: t('explore.plusRequiredSubtitle'),
          });
        } else {
          openModal('PricingModal', { title: t('explore.limitReached'), subtitle: t('explore.limitReachedSubtitle') });
        }
      } else addToast(err.message || 'Generation failed', 'error');
    } finally { setIsCloning(false); }
  }, [remixInput, isCloning, resolvedItem, modelSlug, activeMediaType, musicDescription, localParams, buildFinalPrompt, addToast, t, router, openModal, closeExploreLightbox]);

  const handleRemixSubmit = useCallback(() => {
    if (!remixInput.trim() || isCloning) return;
    if (requireAuth('generate')) return;
    if (requirePlusFn()) return;

    if (activeMediaType === 'video') {
      const copy = getExploreVideoConfirmCopy(t, modelSlug, localParams);
      openModal('ConfirmationModal', {
        title: copy.title,
        message: copy.message,
        confirmText: copy.confirmText,
        cancelText: copy.cancelText,
        onConfirm: () => {
          closeModal();
          runRemixSubmit();
        },
      });
      return;
    }

    runRemixSubmit();
  }, [remixInput, isCloning, requireAuth, requirePlusFn, resolvedItem, activeMediaType, modelSlug, localParams, t, openModal, closeModal, runRemixSubmit]);

  const handleCategoryPress = useCallback((slug) => {
    closeExploreLightbox();
    router.push(`/explore/category/${encodeURIComponent(slug)}`);
  }, [closeExploreLightbox, router]);

  const handleModelPress = useCallback((slug) => {
    if (!slug) return;
    closeExploreLightbox();
    router.push(`/explore?model=${encodeURIComponent(slug)}`);
  }, [closeExploreLightbox, router]);

  const handleCopyPrompt = useCallback(async () => {
    if (!resolvedItem?.prompt) return;
    trackEvent('explore', 'explore_copy_prompt', resolvedItem.slug);
    try { await copyText(resolvedItem.prompt); mediumTap(); addToast('Prompt copied', 'success'); }
    catch { addToast('Failed to copy', 'error'); }
  }, [resolvedItem, addToast]);

  const handleDownload = useCallback(() => {
    if (!resolvedItem?.slug) return;
    trackEvent('explore', 'explore_download', resolvedItem.slug);
    trackExploreEvent(resolvedItem.slug, 'download');
  }, [resolvedItem, trackExploreEvent]);

  if (!isOpen || !resolvedItem) return null;

  const useStaticMediaHost = Platform.OS === 'macos' && resolvedItem.mediaType === 'video';

  return (
    <Animated.View
      ref={containerRef}
      style={[lbStyles.container, { opacity: fadeAnim }, isWeb && { position: 'fixed' }]}
      {...(Platform.OS === 'macos' ? {
        focusable: true,
        onKeyDown: handleMacOSKeyDown,
      } : {})}
    >
      <Pressable
        style={[
          lbStyles.backdrop,
          // Music gets a near-opaque scrim so the orbs read as the entire
          // viewport's lighting; other media types stay slightly translucent
          // so the blurhash / surrounding page can show through.
          { backgroundColor: resolvedItem.mediaType === 'music' ? 'rgba(0,0,0,0.975)' : 'rgba(0,0,0,0.88)' },
        ]}
        onPress={handleClose}
      />

      {/* Blurhash ambient background */}
      {resolvedItem.blurhash && (
        <View style={lbStyles.blurhashBg} pointerEvents="none">
          <Image
            key={resolvedItem.id}
            source={null}
            placeholder={{ blurhash: resolvedItem.blurhash }}
            style={[StyleSheet.absoluteFill, { transform: [{ scale: 1.15 }] }]}
            contentFit="cover"
            transition={0}
          />
          <View style={lbStyles.blurhashOverlay} />
        </View>
      )}

      {/* Close — top left */}
      <Pressable onPress={handleClose} style={[lbStyles.closeBtn, { top: topButtonOffset }]}>
        <Icon name="xmark" size={18} color="#fff" />
      </Pressable>

      {/* Favorite (heart). Desktop: top-left next to close. Compact: top-right
          alongside the download button so the right cluster is the action zone. */}
      <Pressable
        onPress={handleToggleFavorite}
        style={[lbStyles.favoriteBtn, isCompact && lbStyles.favoriteBtnCompact, { top: topButtonOffset }]}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={isFavorited ? 'Remove from favorites' : 'Save to favorites'}
      >
        <Animated.View style={{ transform: [{ scale: heartScaleAnim }] }}>
          <Icon
            name="heart"
            size={18}
            color={isFavorited ? '#ef4444' : '#fff'}
            solid={isFavorited}
          />
        </Animated.View>
      </Pressable>

      {/* Download — compact only. Top-right corner, matches the close/heart pill style. */}
      {isCompact && (
        <Pressable
          onPress={handleTopDownload}
          style={[lbStyles.downloadBtn, { top: topButtonOffset }]}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Download"
        >
          <Animated.View style={topDownloading ? topDownloadSpinStyle : undefined}>
            <Icon name={topDownloading ? 'circle-notch' : 'download'} size={17} color="#fff" />
          </Animated.View>
        </Pressable>
      )}

      {/* Media pane — fills remaining space left of the panel */}
      <View
        style={[
          lbStyles.mediaArea,
          isCompact && { right: 0 },
          // On compact, the bottom-sheet panel covers the lower portion of
          // the media area. For videos that's a problem because the native
          // playback controls render at the bottom of the player and become
          // unreachable. Lift the video bottom above the peek panel so the
          // scrubber/skip controls stay tappable. (Images & music gradient
          // look fine letterboxed, so we only do this for videos.)
          isCompact && resolvedItem?.mediaType === 'video' && {
            bottom: (winH - (compactPanelExpanded ? compactExpandedTop : compactPeekTop)) + 12,
          },
        ]}
        {...(isCompact ? swipePanResponder.panHandlers : {})}
      >
        {useStaticMediaHost ? (
          <View
            style={[
              StyleSheet.absoluteFill,
              { justifyContent: 'center', alignItems: 'center', padding: isCompact ? 10 : 10 },
            ]}
          >
            <LightboxMedia key={resolvedItem.id} item={resolvedItem} colors={colors} />
          </View>
        ) : (
          <Animated.View
            style={[
              StyleSheet.absoluteFill,
              { opacity: mediaFadeAnim, justifyContent: 'center', alignItems: 'center', padding: isCompact ? 10 : 10 },
              isCompact && {
                transform: [{ translateY: swipeY }, { scale: swipeMediaScale }],
                opacity: compactMediaOpacity,
              },
            ]}
          >
            {/* Key on item id so navigating to a new item unmounts the previous
                media player — this fires LightboxMusic / LightboxVideo cleanup
                (unloading audio / releasing the video player) and mounts a
                fresh instance that auto-plays the new item. */}
            <LightboxMedia key={resolvedItem.id} item={resolvedItem} colors={colors} />
          </Animated.View>
        )}

        {/* Vertical nav arrows — right edge of media (left of panel) */}
        {!isCompact && (hasPrev || hasNext) && (
          <View style={lbStyles.navColumn}>
            {hasPrev ? (
              <Pressable onPress={() => handleNavigate(items[currentIndex - 1])} style={lbStyles.navArrow}>
                <Icon name="chevron-up" size={18} color="#fff" />
              </Pressable>
            ) : <View style={lbStyles.navArrowPlaceholder} />}
            {hasNext ? (
              <Pressable onPress={navigateForward} style={lbStyles.navArrow}>
                <Icon name="chevron-down" size={18} color="#fff" />
              </Pressable>
            ) : <View style={lbStyles.navArrowPlaceholder} />}
          </View>
        )}
      </View>

      {/* Right panel — full height on desktop, animated bottom-sheet on compact */}
      <Animated.View style={[
        lbStyles.panel,
        { backgroundColor: getShadowThemeStyles(theme).primaryOpaque(0.95) }, // F2 = 0.95 alpha in hex
        isCompact && lbStyles.panelCompact,
        isCompact && { top: compactPanelTop, overflow: 'hidden' },
      ]}>

        <DetailPanelHeader
          resolvedItem={resolvedItem}
          colors={colors}
          scrolled={panelScrolled}
          onModelPress={handleModelPress}
          isCompact={isCompact}
          isExpanded={compactPanelExpanded}
          onToggleExpand={toggleCompactPanel}
        />

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={lbStyles.panelScroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          onScroll={handlePanelScroll}
          scrollEventThrottle={16}
        >
          <DetailPanelContent
            key={resolvedItem.id}
            resolvedItem={resolvedItem}
            colors={colors}
            activeMediaType={activeMediaType}
            setActiveMediaType={setActiveMediaType}
            modelSlug={modelSlug}
            setModelSlug={setModelSlug}
            localParams={localParams}
            setLocalParams={setLocalParams}
            musicDescription={musicDescription}
            setMusicDescription={setMusicDescription}
            musicLyrics={musicLyrics}
            setMusicLyrics={setMusicLyrics}
            lyricsExpanded={lyricsExpanded}
            setLyricsExpanded={setLyricsExpanded}
            handleCopyPrompt={handleCopyPrompt}
            isPlus={isPlus}
            onCategoryPress={handleCategoryPress}
            onDownload={handleDownload}
            modelSelectorInputs={modelSelectorInputs}
          />
        </ScrollView>

        <StickyPromptInput
          remixInput={remixInput}
          setRemixInput={setRemixInput}
          mediaType={activeMediaType}
          setMediaType={setActiveMediaType}
          onRemix={handleRemixSubmit}
          onUsePrompt={handleUsePrompt}
          isSubmitting={isCloning}
          colors={colors}
          theme={theme}
          itemMediaType={resolvedItem.mediaType}
          clones={resolvedItem.stats?.clones || 0}
        />
      </Animated.View>

      {/* Mobile vertical nav — hidden while the sheet is expanded so it doesn't
          peek out behind the sheet edge or get tapped by accident. */}
      {isCompact && !compactPanelExpanded && (hasPrev || hasNext) && (
        <View style={lbStyles.mobileNav}>
          {hasPrev ? (
            <Pressable onPress={() => handleNavigate(items[currentIndex - 1])} style={lbStyles.mobileNavBtn}>
              <Icon name="chevron-up" size={18} color="#fff" />
            </Pressable>
          ) : <View style={{ width: 42 }} />}
          {hasNext ? (
            <Pressable onPress={navigateForward} style={lbStyles.mobileNavBtn}>
              <Icon name="chevron-down" size={18} color="#fff" />
            </Pressable>
          ) : <View style={{ width: 42 }} />}
        </View>
      )}
    </Animated.View>
  );
});

const PANEL_W = 380;

const lbStyles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    top: 0, left: 0, right: 0, bottom: 0,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.88)',
  },
  blurhashBg: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 0,
    overflow: 'hidden',
  },
  blurhashOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.35)',
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    left: 16,
    zIndex: 20,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb && { cursor: 'pointer', transition: 'background-color 0.15s' }),
  },
  favoriteBtn: {
    position: 'absolute',
    top: 16,
    left: 64,
    zIndex: 20,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb && { cursor: 'pointer', transition: 'background-color 0.15s, transform 0.15s' }),
  },
  // Compact: heart moves to top-right (sits just left of the download button).
  // `left: undefined` cancels the default left anchor so `right: 64` wins.
  favoriteBtnCompact: {
    left: undefined,
    right: 64,
  },
  downloadBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 20,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb && { cursor: 'pointer', transition: 'background-color 0.15s' }),
  },
  mediaArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: PANEL_W + 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navColumn: {
    position: 'absolute',
    right: 16,
    top: '50%',
    marginTop: -52,
    gap: 10,
    zIndex: 10,
  },
  navArrow: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    ...(isWeb && { cursor: 'pointer', transition: 'background-color 0.2s' }),
  },
  navArrowPlaceholder: {
    width: 44,
    height: 44,
  },
  panel: {
    position: 'absolute',
    top: 10,
    right: 10,
    bottom: 10,
    borderRadius: 10,
    width: PANEL_W,
    zIndex: 15,
  },
  panelCompact: {
    // Edge-to-edge bottom sheet: full width, anchored to the bottom of the
    // viewport. `top` is animated by the parent (compactPanelTop) — see JSX.
    // Top corners stay rounded so the sheet rises out of the bottom edge with
    // a soft lip; bottom corners are squared off since the sheet meets the
    // screen edge.
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderTopWidth: 1,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  panelScroll: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 8,
    gap: 14,
  },
  mobileNav: {
    position: 'absolute',
    right: 12,
    top: '20%',
    gap: 8,
    zIndex: 20,
  },
  mobileNavBtn: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: 'rgba(0,0,0,0.42)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

ExploreLightbox.displayName = 'ExploreLightbox';

export default ExploreLightbox;
